/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Platform API
 * Scrivio Platform API for cross-cutting concerns like NAW lookup via SBV-Z.  ## Authentication All endpoints require a Firebase Authentication JWT token in the `Authorization: Bearer <token>` header, except where an operation explicitly declares no `security` (the self-service password-reset request and the sign-in method lookup, both by definition made by someone who has not signed in).  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * A koppeling with the secret just issued for it. This response is the only place the secret ever appears; Scrivio does not store it. 
 */
export interface ApiKoppelingCredentialsResponse {
    'koppeling': ApiKoppelingResponse;
    /**
     * The `client_secret` to request tokens with.
     */
    'clientSecret': string;
    /**
     * The token endpoint the integration exchanges its client id and secret at.
     */
    'tokenUrl': string;
}
/**
 * An API-koppeling (ADR-045), never with its secret.
 */
export interface ApiKoppelingResponse {
    'id': string;
    'name': string;
    /**
     * The `client_id` the integration requests its tokens with.
     */
    'clientId': string;
    'status': ApiKoppelingStatus;
    'roles': Array<ApiKoppelingRole>;
    /**
     * When the koppeling stops authenticating, in milliseconds since epoch.
     */
    'expiresAt': number;
    /**
     * When a token of the koppeling last reached the API, to the minute. Null if never.
     */
    'lastUsedAt'?: number | null;
    'revokedAt'?: number | null;
    'createdAt': number;
    /**
     * Who created it; null when that user no longer exists.
     */
    'createdByName'?: string | null;
}


/**
 * A role the koppeling\'s service user holds.
 */
export interface ApiKoppelingRole {
    'id': string;
    'name': string;
}
/**
 * Derived at read time from the revocation and the expiry.
 */

export const ApiKoppelingStatus = {
    Active: 'ACTIVE',
    Expired: 'EXPIRED',
    Revoked: 'REVOKED'
} as const;

export type ApiKoppelingStatus = typeof ApiKoppelingStatus[keyof typeof ApiKoppelingStatus];


export interface ApiKoppelingTermsResponse {
    /**
     * True once the organization accepted the editions covering koppelingen; until then a create is refused.
     */
    'accepted': boolean;
}
/**
 * Where a person signs in (ADR-045 §7): Firebase Authentication or Scrivio\'s own Keycloak. `FIREBASE` for everyone until Scrivio staff switch an organisation or an admin user. 
 */

export const AuthProvider = {
    Firebase: 'FIREBASE',
    Keycloak: 'KEYCLOAK'
} as const;

export type AuthProvider = typeof AuthProvider[keyof typeof AuthProvider];


export interface CreateApiKoppelingRequest {
    /**
     * The practice\'s own label; shown as \"Integratie <naam>\" wherever the koppeling acts.
     */
    'name': string;
    /**
     * Roles of the organization the koppeling acts with; none may grant more than the caller holds.
     */
    'roleIds': Array<string>;
    /**
     * In milliseconds since epoch; in the future and at most twelve months ahead.
     */
    'expiresAt': number;
}
/**
 * Gender as registered in BRP (Basisregistratie Personen): - M: Male (Man) - V: Female (Vrouw) - O: Unknown/Other (Onbekend) 
 */

export const Geslacht = {
    M: 'M',
    V: 'V',
    O: 'O'
} as const;

export type Geslacht = typeof Geslacht[keyof typeof Geslacht];


/**
 * NAW (Name-Address-Residence) data from SBV-Z
 */
export interface NawData {
    /**
     * Family name (surname)
     */
    'geslachtsnaam': string;
    /**
     * Name prefix (e.g., \"van\", \"de\", \"van der\")
     */
    'voorvoegsel'?: string | null;
    /**
     * First names (all given names)
     */
    'voornamen': string;
    /**
     * Date of birth (ISO 8601 format)
     */
    'geboortedatum': string;
    'geslacht': Geslacht;
    /**
     * Street name
     */
    'straatnaam'?: string | null;
    /**
     * House number
     */
    'huisnummer'?: string | null;
    /**
     * House number addition (e.g., \"A\", \"bis\", \"II\")
     */
    'huisnummerToevoeging'?: string | null;
    /**
     * Dutch postal code (format 1234 AB or 1234AB)
     */
    'postcode'?: string | null;
    /**
     * City/town of residence
     */
    'woonplaats'?: string | null;
}


/**
 * Request to look up NAW data by BSN
 */
export interface NawLookupRequest {
    /**
     * Burgerservicenummer (BSN) - 9-digit Dutch citizen service number. Must pass the elfproef (11-check) validation, which OpenAPI cannot express: the weighted digit sum 9·d1 + 8·d2 + … + 2·d8 − d9 is positive and divisible by 11. A BSN that does not is refused with `400` (#3585). 
     */
    'bsn': string;
}
/**
 * Response from NAW lookup
 */
export interface NawLookupResponse {
    'status': NawLookupStatus;
    'data'?: NawData;
    /**
     * Additional message providing context for the lookup result. Present when status is \'found_with_discrepancy\' or \'error\'. 
     */
    'message'?: string | null;
}


/**
 * Status of the NAW lookup: - found: Person found with complete and consistent data - found_with_discrepancy: Person found but data may have inconsistencies - not_found: No person found with the given BSN - error: Lookup failed due to technical issues 
 */

export const NawLookupStatus = {
    Found: 'found',
    FoundWithDiscrepancy: 'found_with_discrepancy',
    NotFound: 'not_found',
    Error: 'error'
} as const;

export type NawLookupStatus = typeof NawLookupStatus[keyof typeof NawLookupStatus];


/**
 * Request to send a password-reset link to an email address
 */
export interface PasswordResetRequest {
    /**
     * Email address of the account to recover
     */
    'email': string;
}
export interface ProblemDetail {
    /**
     * A URI reference that identifies the problem type
     */
    'type': string;
    /**
     * A short, human-readable summary of the problem
     */
    'title': string;
    /**
     * The HTTP status code
     */
    'status': number;
    /**
     * A human-readable explanation specific to this occurrence, in the caller\'s language
     */
    'detail'?: string;
    /**
     * A URI reference that identifies the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    'field'?: string;
    'message'?: string;
}
export interface RotateApiKoppelingSecretRequest {
    /**
     * The new expiry, in milliseconds since epoch; in the future and at most twelve months ahead.
     */
    'expiresAt': number;
}
/**
 * Which sign-in is being asked about: `APP` is the behandelaar app (switched per organisation), `ADMIN` the admin portal (switched per admin user). 
 */

export const SignInAudience = {
    App: 'APP',
    Admin: 'ADMIN'
} as const;

export type SignInAudience = typeof SignInAudience[keyof typeof SignInAudience];


/**
 * The e-mailadres someone typed on a sign-in page, and which sign-in page that is
 */
export interface SignInMethodRequest {
    /**
     * The e-mailadres to sign in with
     */
    'email': string;
    'audience': SignInAudience;
}


/**
 * The provider to sign in with
 */
export interface SignInMethodResponse {
    'provider': AuthProvider;
}


/**
 * The SnelStart page that activates the productiekoppeling.
 */
export interface SnelStartActivationResponse {
    /**
     * SnelStart\'s activation page, carrying this activation\'s single-use referenceKey.
     */
    'activationUrl': string;
}
/**
 * What became of the latest \"Koppelen met SnelStart\", where the connection does not say it: - PENDING: handed to SnelStart, whose webhook may still deliver - NOT_COMPLETED: SnelStart never delivered a usable key before the reference expired; try again 
 */

export const SnelStartActivationState = {
    Pending: 'PENDING',
    NotCompleted: 'NOT_COMPLETED'
} as const;

export type SnelStartActivationState = typeof SnelStartActivationState[keyof typeof SnelStartActivationState];


/**
 * What asking SnelStart came to: - CONNECTED: SnelStart accepted both keys and answered - NOT_CONNECTED: no koppelingssleutel is stored - MAATWERK_SUBSCRIPTION_KEY_MISSING: see `SnelStartMissingKey` - PLATFORM_SUBSCRIPTION_KEY_MISSING: see `SnelStartMissingKey` - CONNECTION_REFUSED: the koppelingssleutel is wrong, or was invalidated by restoring a backup in SnelStart - UNAVAILABLE: SnelStart did not answer; trying again later may work - REJECTED: SnelStart refused the request; `rejection` carries its message - NOT_ENABLED: this deployment has no SnelStart connection switched on - AWAITING_CONFIRMATION: a key SnelStart delivered whose administratie no admin has confirmed; it books nothing yet 
 */

export const SnelStartCheckOutcome = {
    Connected: 'CONNECTED',
    NotConnected: 'NOT_CONNECTED',
    MaatwerkSubscriptionKeyMissing: 'MAATWERK_SUBSCRIPTION_KEY_MISSING',
    PlatformSubscriptionKeyMissing: 'PLATFORM_SUBSCRIPTION_KEY_MISSING',
    ConnectionRefused: 'CONNECTION_REFUSED',
    Unavailable: 'UNAVAILABLE',
    Rejected: 'REJECTED',
    NotEnabled: 'NOT_ENABLED',
    AwaitingConfirmation: 'AWAITING_CONFIRMATION'
} as const;

export type SnelStartCheckOutcome = typeof SnelStartCheckOutcome[keyof typeof SnelStartCheckOutcome];


/**
 * What SnelStart answered when asked over the organization\'s connection.
 */
export interface SnelStartCheckResult {
    'outcome': SnelStartCheckOutcome;
    'route'?: SnelStartRoute;
    /**
     * SnelStart\'s own message for a REJECTED outcome, verbatim. Null otherwise.
     */
    'rejection'?: string | null;
    'testedAt': string;
}


/**
 * The grootboekrekening chosen for verzekeraarfacturen.
 */
export interface SnelStartChosenGrootboek {
    /**
     * SnelStart\'s id for the account — what a verkoopboeking references.
     */
    'id': string;
    'nummer'?: number | null;
    'omschrijving'?: string | null;
}
/**
 * A concern and its labels, or — without a `concernId` — the insurers that belong to no concern. 
 */
export interface SnelStartConcernRelatieGroup {
    'concernId'?: string | null;
    'concernNaam'?: string | null;
    'relatie'?: SnelStartRelatieRef;
    'insurers': Array<SnelStartInsurerRelatieRow>;
}
/**
 * A koppelingssleutel the practice generated in SnelStart.
 */
export interface SnelStartConnectRequest {
    /**
     * The koppelingssleutel. Written to Secret Manager; never returned.
     */
    'connectionKey': string;
}
/**
 * The organization\'s SnelStart connection. Never carries a key.
 */
export interface SnelStartConnectionResponse {
    /**
     * Whether a koppelingssleutel is stored.
     */
    'connected': boolean;
    /**
     * A key SnelStart\'s webhook delivered, whose administratie no admin has confirmed. It books nothing until confirmed. 
     */
    'awaitingConfirmation': boolean;
    /**
     * The administratie a productiekoppeling\'s key belongs to, as SnelStart names it.
     */
    'administratieNaam'?: string | null;
    /**
     * The practice removed the koppeling in SnelStart, which removed it here.
     */
    'disconnectedInSnelStart': boolean;
    /**
     * Whether \"Koppelen met SnelStart\" is offered in this deployment.
     */
    'activationAvailable': boolean;
    'activation'?: SnelStartActivationState;
    'route'?: SnelStartRoute;
    'missingKey'?: SnelStartMissingKey;
    'grootboek'?: SnelStartChosenGrootboek;
}


export interface SnelStartGrootboekChoiceRequest {
    /**
     * SnelStart\'s id of an active grootboekrekening in the administratie.
     */
    'grootboekId': string;
}
/**
 * One active grootboekrekening in the practice\'s administratie.
 */
export interface SnelStartGrootboekOption {
    'id': string;
    'nummer'?: number | null;
    'omschrijving'?: string | null;
    /**
     * Whether its grootboekfunctie is one of SnelStart\'s `Verkopen…` functions.
     */
    'omzetrekening': boolean;
}
/**
 * What became of a chosen grootboekrekening. Anything but ACTIEF refuses booking. - ACTIEF: still an active account - NONACTIEF: deactivated in SnelStart since it was chosen - VERDWENEN: no longer in the administratie 
 */

export const SnelStartGrootboekStatus = {
    Actief: 'ACTIEF',
    Nonactief: 'NONACTIEF',
    Verdwenen: 'VERDWENEN'
} as const;

export type SnelStartGrootboekStatus = typeof SnelStartGrootboekStatus[keyof typeof SnelStartGrootboekStatus];


/**
 * The grootboekrekeningen on offer, in offering order.
 */
export interface SnelStartGrootboekenResponse {
    'check': SnelStartCheckResult;
    'grootboeken': Array<SnelStartGrootboekOption>;
    /**
     * The account to preselect (8005, when the administratie has it active).
     */
    'suggestedGrootboekId'?: string | null;
    'chosenGrootboekStatus'?: SnelStartGrootboekStatus;
}


/**
 * One zorgverzekeraar and the SnelStart relatie its verzekeraarfacturen are booked on.
 */
export interface SnelStartInsurerRelatieRow {
    'insurerId': string;
    'uzoviCode': string;
    'naam': string;
    /**
     * Whether the practice has declared a batch to this insurer.
     */
    'billed': boolean;
    'ownRelatie'?: SnelStartRelatieRef;
    'resolvedRelatie'?: SnelStartRelatieRef;
    'resolvedFrom'?: SnelStartRelatieBron;
}


/**
 * Which key a connection lacks. Neither subscription key ever stands in for the other. - CONNECTION_KEY: the practice has not connected - MAATWERK_SUBSCRIPTION_KEY: a Scrivio platform admin has not stored this practice\'s key - PLATFORM_SUBSCRIPTION_KEY: this deployment has no platform subscription key 
 */

export const SnelStartMissingKey = {
    ConnectionKey: 'CONNECTION_KEY',
    MaatwerkSubscriptionKey: 'MAATWERK_SUBSCRIPTION_KEY',
    PlatformSubscriptionKey: 'PLATFORM_SUBSCRIPTION_KEY'
} as const;

export type SnelStartMissingKey = typeof SnelStartMissingKey[keyof typeof SnelStartMissingKey];


/**
 * Where an insurer\'s relatie comes from: - INSURER: mapped to the insurer itself - CONCERN: mapped to its concern, and the insurer has none of its own 
 */

export const SnelStartRelatieBron = {
    Insurer: 'INSURER',
    Concern: 'CONCERN'
} as const;

export type SnelStartRelatieBron = typeof SnelStartRelatieBron[keyof typeof SnelStartRelatieBron];


export interface SnelStartRelatieMappingsResponse {
    /**
     * Groups with a billed insurer first; within a group, billed insurers first.
     */
    'groups': Array<SnelStartConcernRelatieGroup>;
    /**
     * Billed insurers no relatie resolves for — what would not book.
     */
    'unmappedBilledInsurerIds': Array<string>;
}
/**
 * A relatie in the practice\'s SnelStart administratie. `id` is SnelStart\'s own and what a verkoopboeking references; it is absent only for a relatiecode typed by hand while SnelStart could not be reached. A request carries `id`, `relatiecode` or both. `relatiecode` and `naam` are for display. 
 */
export interface SnelStartRelatieRef {
    'id'?: string | null;
    'relatiecode'?: number | null;
    'naam'?: string | null;
}
/**
 * The klanten a search in SnelStart found. Empty unless `check` is CONNECTED.
 */
export interface SnelStartRelatieSearchResponse {
    'check': SnelStartCheckResult;
    'relaties': Array<SnelStartRelatieRef>;
}
/**
 * How the koppelingssleutel reached Scrivio, which decides the subscription key sent with it: - MAATWERK: pasted by the practice; sends the per-organisation key a platform admin stored - PRODUCTIE: delivered by SnelStart\'s oAuth webhook; sends Scrivio\'s platform key 
 */

export const SnelStartRoute = {
    Maatwerk: 'MAATWERK',
    Productie: 'PRODUCTIE'
} as const;

export type SnelStartRoute = typeof SnelStartRoute[keyof typeof SnelStartRoute];


/**
 * The current user\'s Zivver enablement and the outcome of the most recent address validation against the organization\'s Zivver tenant. 
 */
export interface UserZivverStatusResponse {
    /**
     * Whether the user has opted in to Zivver sending.
     */
    'enabled': boolean;
    'validationStatus': UserZivverValidationStatus;
    /**
     * The email confirmed as an active Zivver user. Null when not validated.
     */
    'validatedFromAddress'?: string | null;
    /**
     * When the address was last validated. Null when never validated.
     */
    'lastValidatedAt'?: string | null;
}


/**
 * Request to enable or disable Zivver sending for the current user.
 */
export interface UserZivverUpdateRequest {
    /**
     * Whether the user opts in to sending via the organization\'s Zivver tenant.
     */
    'enabled': boolean;
}
/**
 * Outcome of the most recent per-user Zivver validation: - VALIDATED: the user\'s address was confirmed as an active Zivver user - NOT_VALIDATED: enabled but not (yet) validated (e.g. SCIM unavailable) - DISABLED: the user has not opted in 
 */

export const UserZivverValidationStatus = {
    Validated: 'VALIDATED',
    NotValidated: 'NOT_VALIDATED',
    Disabled: 'DISABLED'
} as const;

export type UserZivverValidationStatus = typeof UserZivverValidationStatus[keyof typeof UserZivverValidationStatus];


/**
 * Whether the caller can send a letter to a huisarts over ZorgMail (#4121, ADR-041 §3), and whether the practice\'s ZorgMail inbox is offered (ADR-050).  No per-user enablement beside these: unlike Zivver there is nothing to opt in to, because a ZorgMail afzender is a name in the message rather than an identity registered with the provider. 
 */
export interface UserZorgMailAvailabilityResponse {
    /**
     * True when the caller\'s organization has a FULL license and a recorded ZorgMail Account-ID. It says nothing about whether Enovation have permitted Scrivio to send on that practice\'s behalf — that is recorded at their end and answered by the first send. 
     */
    'available': boolean;
    /**
     * True when the caller\'s organization has a FULL license, a stored Secure e-mail mailbox login, and that login has synced successfully at least once (ADR-050). A later failed sync leaves it true — the inbox shows why it is stale; saving a new login or removing it makes it false until the next successful sync. Whether the caller may *read* the inbox is `zorgmail_inbox.read`, a separate question. 
     */
    'inboxAvailable': boolean;
}
/**
 * The organization\'s VECOZO COV connection prerequisites (#3016): whether the practice confirmed the aansluitovereenkomst and toestemmingsverklaring are arranged, and the AGB-code that toestemmingsverklaring has to name (ADR-031 Decision 1). 
 */
export interface VecozoCovAgreementResponse {
    /**
     * Whether the practice has confirmed its VECOZO COV connection is arranged.
     */
    'agreementConfirmed': boolean;
    /**
     * When the confirmation was recorded, in milliseconds since epoch. Null while it is not confirmed. Re-confirming does not move it. 
     */
    'confirmedAt'?: number | null;
    /**
     * The user who recorded the confirmation. Null while it is not confirmed.
     */
    'confirmedByUserId'?: string | null;
    /**
     * The organization\'s AGB-code, which the toestemmingsverklaring at VECOZO has to be granted for. Null when the organization holds none, in which case COV cannot be used whatever this agreement says. 
     */
    'agbCode'?: string | null;
}
/**
 * Request to record or withdraw the practice\'s confirmation that it arranged its VECOZO COV aansluiting and toestemmingsverklaring. 
 */
export interface VecozoCovAgreementUpdateRequest {
    /**
     * True records the confirmation, false withdraws it. Deliberately carries no default: an omitted field must fail validation rather than silently withdraw a confirmation the practice never touched. 
     */
    'agreementConfirmed': boolean;
}
/**
 * The organization\'s Zivver SMTP configuration. The password is never included; `configured` indicates whether credentials are stored. 
 */
export interface ZivverConfigResponse {
    /**
     * Whether a Zivver SMTP password is currently stored for the organization.
     */
    'configured': boolean;
    /**
     * SMTP host. Null when not configured.
     */
    'host'?: string | null;
    /**
     * SMTP submission port. Null when not configured.
     */
    'port'?: number | null;
    /**
     * SMTP username. Null when not configured.
     */
    'username'?: string | null;
    'defaultVerificationMethod'?: ZivverVerificationMethod;
}


/**
 * Request to create or update the Zivver SMTP configuration.
 */
export interface ZivverConfigUpdateRequest {
    /**
     * SMTP host. Defaults to Zivver\'s submission endpoint when omitted.
     */
    'host'?: string;
    /**
     * SMTP submission port. Defaults to 587 when omitted.
     */
    'port'?: number;
    /**
     * SMTP username issued by Zivver (per-organization UUID).
     */
    'username': string;
    /**
     * SMTP password issued by Zivver. Written to Secret Manager and never stored in the database or returned by any endpoint. 
     */
    'password': string;
    'defaultVerificationMethod'?: ZivverVerificationMethod;
}


/**
 * Result of a Zivver SMTP connection test (no message is sent).
 */
export interface ZivverTestResult {
    /**
     * Whether an authenticated SMTP session was established.
     */
    'success': boolean;
    /**
     * Coarse failure classification (present only on failure): - AUTHENTICATION: SMTP AUTH was rejected (wrong/revoked credentials) - CONNECTION: could not establish/maintain the connection - CONFIGURATION: no Zivver configuration is stored 
     */
    'category'?: ZivverTestResultCategoryEnum | null;
    /**
     * Human-readable, PII-free explanation of the failure. Null on success.
     */
    'errorReason'?: string | null;
    /**
     * When the test was performed.
     */
    'testedAt': string;
}

export const ZivverTestResultCategoryEnum = {
    Authentication: 'AUTHENTICATION',
    Connection: 'CONNECTION',
    Configuration: 'CONFIGURATION'
} as const;

export type ZivverTestResultCategoryEnum = typeof ZivverTestResultCategoryEnum[keyof typeof ZivverTestResultCategoryEnum];

/**
 * Default recipient-verification method Zivver should apply to messages sent by this organization (ADR-028): - SMS: one-time code by text message - EMAIL: e-mail verification - ACCESS_CODE: pre-agreed access code - ZIVVER_ACCOUNT: recipient logs in with a Zivver account 
 */

export const ZivverVerificationMethod = {
    Sms: 'SMS',
    Email: 'EMAIL',
    AccessCode: 'ACCESS_CODE',
    ZivverAccount: 'ZIVVER_ACCOUNT'
} as const;

export type ZivverVerificationMethod = typeof ZivverVerificationMethod[keyof typeof ZivverVerificationMethod];


/**
 * The organization\'s ZorgDomein auto-activation state (ADR-040 section 6, #4106). Auto-activation is a sequence the practice drives from ZorgDomein\'s side; this is everything Scrivio can tell them about where it stands. 
 */
export interface ZorgDomeinActivationResponse {
    /**
     * The stable, invariable identifier Scrivio answers ZorgDomein\'s organisation search with, and which ZorgDomein store and name in the `ZD Destination` extension of every document afterwards. It is `organizations.id` and never changes — deliberately not derived from the AGB-code or the KvK-number, either of which a practice can correct. 
     */
    'organizationIdentifier': string;
    /**
     * The organization\'s AGB-code, one of the two identifiers ZorgDomein can find this practice on. Null when the organization holds none. 
     */
    'agbCode'?: string | null;
    /**
     * The organization\'s KvK-number, the other identifier ZorgDomein can find this practice on. Null when the organization holds none; a practice holding neither cannot be found by the search at all. 
     */
    'kvkNumber'?: string | null;
    /**
     * The code ZorgDomein most recently sent for this organization, which the practice types back into ZorgDomein to complete activation. Null until a `ZD Activation Verification` document has been received — a normal state part-way through the sequence, not an error. A newer code replaces an older one. 
     */
    'verificationCode'?: string | null;
    /**
     * When that code arrived, in milliseconds since epoch. Null while there is no code. Shown beside it because the code is short-lived and ZorgDomein state no lifetime, so how fresh it is is the only thing that says whether it can still be used. 
     */
    'verificationCodeReceivedAt'?: number | null;
}
/**
 * The practice\'s ZorgMail configuration: its Account-ID (ADR-041 §3) and whether its Secure e-mail mailbox login is stored (ADR-050 §4). The mailbox password is never included. 
 */
export interface ZorgMailConfigResponse {
    /**
     * Whether an Account-ID is stored for this practice.
     */
    'configured': boolean;
    /**
     * The stored Account-ID. Null when not configured.
     */
    'accountId'?: string | null;
    /**
     * Whether a Secure e-mail mailbox login is stored for this practice (ADR-050 §4).
     */
    'mailboxConfigured': boolean;
    /**
     * The stored mailbox username. Null when no login is stored.
     */
    'mailboxUsername'?: string | null;
    /**
     * The IMAPS host the mailbox is reached on. Null when no login is stored.
     */
    'mailboxHost'?: string | null;
}
/**
 * Request to record the practice\'s own ZorgMail Account-ID (#4121, ADR-041 §3).
 */
export interface ZorgMailConfigUpdateRequest {
    /**
     * The practice\'s ZorgMail Account-ID, digits only. It becomes the local part of the SMTP `MAIL FROM` address, which is what tells Enovation whose behalf a message is sent on.  Nine digits in every example Enovation give, and the width is deliberately **not** constrained: a well-formed but wrong id is theirs to refuse, and they do so legibly, in a way that pinning a width here would replace with a local error that is wrong. 
     */
    'accountId': string;
}
/**
 * - OK: the login was accepted and the folders listed - AUTHENTICATION_FAILED: the server refused the username or password - UNREACHABLE: no connection could be made to the host - TLS_FAILED: the TLS handshake failed, typically a certificate that is not trusted - NOT_CONFIGURED: no mailbox login is stored 
 */

export const ZorgMailMailboxTestOutcome = {
    Ok: 'OK',
    AuthenticationFailed: 'AUTHENTICATION_FAILED',
    Unreachable: 'UNREACHABLE',
    TlsFailed: 'TLS_FAILED',
    NotConfigured: 'NOT_CONFIGURED'
} as const;

export type ZorgMailMailboxTestOutcome = typeof ZorgMailMailboxTestOutcome[keyof typeof ZorgMailMailboxTestOutcome];


/**
 * The outcome of a ZorgMail Secure e-mail mailbox login test.
 */
export interface ZorgMailMailboxTestResult {
    'outcome': ZorgMailMailboxTestOutcome;
    /**
     * When the test was performed.
     */
    'testedAt': string;
}


/**
 * The practice\'s ZorgMail Secure e-mail mailbox login (ADR-050 §4).
 */
export interface ZorgMailMailboxUpdateRequest {
    /**
     * The mailbox username Enovation issued.
     */
    'username': string;
    /**
     * The mailbox password. Stored in Secret Manager and never returned.
     */
    'password': string;
    /**
     * The IMAPS host, a bare hostname. When omitted or null the stored login uses `mail.zorgmail.nl`. 
     */
    'host'?: string | null;
}

/**
 * ApiKoppelingenApi - axios parameter creator
 */
export const ApiKoppelingenApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Creates the Keycloak client an integration authenticates as (`client_credentials`) and a service user in the organization holding the chosen roles. The secret is in this response and nowhere else: Scrivio does not store it.  Refused with 409 `api-koppeling-terms-not-accepted` until the organization has accepted the editions of the Overeenkomst and Bijlage A that cover koppelingen; with 403 when a chosen role grants a permission the caller does not hold; with 400 `api-koppeling-expiry-out-of-range` unless `expiresAt` lies in the future and at most twelve months ahead; with 503 `api-koppelingen-unavailable` when Keycloak cannot be reached. Requires the `integrations.manage` permission. 
         * @summary Create an API-koppeling
         * @param {string} organizationId Organization ID
         * @param {CreateApiKoppelingRequest} createApiKoppelingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createApiKoppeling: async (organizationId: string, createApiKoppelingRequest: CreateApiKoppelingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('createApiKoppeling', 'organizationId', organizationId)
            // verify required parameter 'createApiKoppelingRequest' is not null or undefined
            assertParamExists('createApiKoppeling', 'createApiKoppelingRequest', createApiKoppelingRequest)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/api-koppelingen`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createApiKoppelingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The koppeling, without its secret. Requires the `integrations.manage` permission.
         * @summary Get one API-koppeling
         * @param {string} organizationId Organization ID
         * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getApiKoppeling: async (organizationId: string, koppelingId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getApiKoppeling', 'organizationId', organizationId)
            // verify required parameter 'koppelingId' is not null or undefined
            assertParamExists('getApiKoppeling', 'koppelingId', koppelingId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/api-koppelingen/{koppelingId}`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"koppelingId"}}`, encodeURIComponent(String(koppelingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Whether the organization has accepted the editions of the Overeenkomst and Bijlage A that cover koppelingen — the condition `createApiKoppeling` refuses with 409 `api-koppeling-terms-not-accepted`, answered before anyone fills in the form. Requires the `integrations.manage` permission. 
         * @summary Whether the organization may create API-koppelingen
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getApiKoppelingTerms: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getApiKoppelingTerms', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/api-koppelingen/terms`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every API-koppeling of the organization, newest first, revoked and expired ones included (ADR-045). A secret is never returned here. Requires the `integrations.manage` permission. 
         * @summary List the organization\'s API-koppelingen
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listApiKoppelingen: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('listApiKoppelingen', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/api-koppelingen`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Revokes the koppeling for good: its tokens are refused from this request on, and its Keycloak client is disabled so no secret obtains a new one. Revoking a revoked koppeling changes nothing. Requires the `integrations.manage` permission. 
         * @summary Revoke an API-koppeling
         * @param {string} organizationId Organization ID
         * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeApiKoppeling: async (organizationId: string, koppelingId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('revokeApiKoppeling', 'organizationId', organizationId)
            // verify required parameter 'koppelingId' is not null or undefined
            assertParamExists('revokeApiKoppeling', 'koppelingId', koppelingId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/api-koppelingen/{koppelingId}/revoke`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"koppelingId"}}`, encodeURIComponent(String(koppelingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Issues a new secret, valid until the new `expiresAt`, and returns it once. The previous secret keeps working for 24 hours, so the integration can switch over without an outage. An expired koppeling is renewed this way; a revoked one is refused with 409 `api-koppeling-revoked`. Requires the `integrations.manage` permission. 
         * @summary Rotate an API-koppeling\'s secret
         * @param {string} organizationId Organization ID
         * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
         * @param {RotateApiKoppelingSecretRequest} rotateApiKoppelingSecretRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        rotateApiKoppelingSecret: async (organizationId: string, koppelingId: string, rotateApiKoppelingSecretRequest: RotateApiKoppelingSecretRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('rotateApiKoppelingSecret', 'organizationId', organizationId)
            // verify required parameter 'koppelingId' is not null or undefined
            assertParamExists('rotateApiKoppelingSecret', 'koppelingId', koppelingId)
            // verify required parameter 'rotateApiKoppelingSecretRequest' is not null or undefined
            assertParamExists('rotateApiKoppelingSecret', 'rotateApiKoppelingSecretRequest', rotateApiKoppelingSecretRequest)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/api-koppelingen/{koppelingId}/secret`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"koppelingId"}}`, encodeURIComponent(String(koppelingId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(rotateApiKoppelingSecretRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ApiKoppelingenApi - functional programming interface
 */
export const ApiKoppelingenApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ApiKoppelingenApiAxiosParamCreator(configuration)
    return {
        /**
         * Creates the Keycloak client an integration authenticates as (`client_credentials`) and a service user in the organization holding the chosen roles. The secret is in this response and nowhere else: Scrivio does not store it.  Refused with 409 `api-koppeling-terms-not-accepted` until the organization has accepted the editions of the Overeenkomst and Bijlage A that cover koppelingen; with 403 when a chosen role grants a permission the caller does not hold; with 400 `api-koppeling-expiry-out-of-range` unless `expiresAt` lies in the future and at most twelve months ahead; with 503 `api-koppelingen-unavailable` when Keycloak cannot be reached. Requires the `integrations.manage` permission. 
         * @summary Create an API-koppeling
         * @param {string} organizationId Organization ID
         * @param {CreateApiKoppelingRequest} createApiKoppelingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createApiKoppeling(organizationId: string, createApiKoppelingRequest: CreateApiKoppelingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ApiKoppelingCredentialsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createApiKoppeling(organizationId, createApiKoppelingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ApiKoppelingenApi.createApiKoppeling']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The koppeling, without its secret. Requires the `integrations.manage` permission.
         * @summary Get one API-koppeling
         * @param {string} organizationId Organization ID
         * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getApiKoppeling(organizationId: string, koppelingId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ApiKoppelingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getApiKoppeling(organizationId, koppelingId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ApiKoppelingenApi.getApiKoppeling']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Whether the organization has accepted the editions of the Overeenkomst and Bijlage A that cover koppelingen — the condition `createApiKoppeling` refuses with 409 `api-koppeling-terms-not-accepted`, answered before anyone fills in the form. Requires the `integrations.manage` permission. 
         * @summary Whether the organization may create API-koppelingen
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getApiKoppelingTerms(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ApiKoppelingTermsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getApiKoppelingTerms(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ApiKoppelingenApi.getApiKoppelingTerms']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every API-koppeling of the organization, newest first, revoked and expired ones included (ADR-045). A secret is never returned here. Requires the `integrations.manage` permission. 
         * @summary List the organization\'s API-koppelingen
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listApiKoppelingen(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ApiKoppelingResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listApiKoppelingen(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ApiKoppelingenApi.listApiKoppelingen']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Revokes the koppeling for good: its tokens are refused from this request on, and its Keycloak client is disabled so no secret obtains a new one. Revoking a revoked koppeling changes nothing. Requires the `integrations.manage` permission. 
         * @summary Revoke an API-koppeling
         * @param {string} organizationId Organization ID
         * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async revokeApiKoppeling(organizationId: string, koppelingId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ApiKoppelingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.revokeApiKoppeling(organizationId, koppelingId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ApiKoppelingenApi.revokeApiKoppeling']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Issues a new secret, valid until the new `expiresAt`, and returns it once. The previous secret keeps working for 24 hours, so the integration can switch over without an outage. An expired koppeling is renewed this way; a revoked one is refused with 409 `api-koppeling-revoked`. Requires the `integrations.manage` permission. 
         * @summary Rotate an API-koppeling\'s secret
         * @param {string} organizationId Organization ID
         * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
         * @param {RotateApiKoppelingSecretRequest} rotateApiKoppelingSecretRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async rotateApiKoppelingSecret(organizationId: string, koppelingId: string, rotateApiKoppelingSecretRequest: RotateApiKoppelingSecretRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ApiKoppelingCredentialsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.rotateApiKoppelingSecret(organizationId, koppelingId, rotateApiKoppelingSecretRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ApiKoppelingenApi.rotateApiKoppelingSecret']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ApiKoppelingenApi - factory interface
 */
export const ApiKoppelingenApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ApiKoppelingenApiFp(configuration)
    return {
        /**
         * Creates the Keycloak client an integration authenticates as (`client_credentials`) and a service user in the organization holding the chosen roles. The secret is in this response and nowhere else: Scrivio does not store it.  Refused with 409 `api-koppeling-terms-not-accepted` until the organization has accepted the editions of the Overeenkomst and Bijlage A that cover koppelingen; with 403 when a chosen role grants a permission the caller does not hold; with 400 `api-koppeling-expiry-out-of-range` unless `expiresAt` lies in the future and at most twelve months ahead; with 503 `api-koppelingen-unavailable` when Keycloak cannot be reached. Requires the `integrations.manage` permission. 
         * @summary Create an API-koppeling
         * @param {string} organizationId Organization ID
         * @param {CreateApiKoppelingRequest} createApiKoppelingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createApiKoppeling(organizationId: string, createApiKoppelingRequest: CreateApiKoppelingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ApiKoppelingCredentialsResponse> {
            return localVarFp.createApiKoppeling(organizationId, createApiKoppelingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * The koppeling, without its secret. Requires the `integrations.manage` permission.
         * @summary Get one API-koppeling
         * @param {string} organizationId Organization ID
         * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getApiKoppeling(organizationId: string, koppelingId: string, options?: RawAxiosRequestConfig): AxiosPromise<ApiKoppelingResponse> {
            return localVarFp.getApiKoppeling(organizationId, koppelingId, options).then((request) => request(axios, basePath));
        },
        /**
         * Whether the organization has accepted the editions of the Overeenkomst and Bijlage A that cover koppelingen — the condition `createApiKoppeling` refuses with 409 `api-koppeling-terms-not-accepted`, answered before anyone fills in the form. Requires the `integrations.manage` permission. 
         * @summary Whether the organization may create API-koppelingen
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getApiKoppelingTerms(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<ApiKoppelingTermsResponse> {
            return localVarFp.getApiKoppelingTerms(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Every API-koppeling of the organization, newest first, revoked and expired ones included (ADR-045). A secret is never returned here. Requires the `integrations.manage` permission. 
         * @summary List the organization\'s API-koppelingen
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listApiKoppelingen(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<ApiKoppelingResponse>> {
            return localVarFp.listApiKoppelingen(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Revokes the koppeling for good: its tokens are refused from this request on, and its Keycloak client is disabled so no secret obtains a new one. Revoking a revoked koppeling changes nothing. Requires the `integrations.manage` permission. 
         * @summary Revoke an API-koppeling
         * @param {string} organizationId Organization ID
         * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeApiKoppeling(organizationId: string, koppelingId: string, options?: RawAxiosRequestConfig): AxiosPromise<ApiKoppelingResponse> {
            return localVarFp.revokeApiKoppeling(organizationId, koppelingId, options).then((request) => request(axios, basePath));
        },
        /**
         * Issues a new secret, valid until the new `expiresAt`, and returns it once. The previous secret keeps working for 24 hours, so the integration can switch over without an outage. An expired koppeling is renewed this way; a revoked one is refused with 409 `api-koppeling-revoked`. Requires the `integrations.manage` permission. 
         * @summary Rotate an API-koppeling\'s secret
         * @param {string} organizationId Organization ID
         * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
         * @param {RotateApiKoppelingSecretRequest} rotateApiKoppelingSecretRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        rotateApiKoppelingSecret(organizationId: string, koppelingId: string, rotateApiKoppelingSecretRequest: RotateApiKoppelingSecretRequest, options?: RawAxiosRequestConfig): AxiosPromise<ApiKoppelingCredentialsResponse> {
            return localVarFp.rotateApiKoppelingSecret(organizationId, koppelingId, rotateApiKoppelingSecretRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ApiKoppelingenApi - object-oriented interface
 */
export class ApiKoppelingenApi extends BaseAPI {
    /**
     * Creates the Keycloak client an integration authenticates as (`client_credentials`) and a service user in the organization holding the chosen roles. The secret is in this response and nowhere else: Scrivio does not store it.  Refused with 409 `api-koppeling-terms-not-accepted` until the organization has accepted the editions of the Overeenkomst and Bijlage A that cover koppelingen; with 403 when a chosen role grants a permission the caller does not hold; with 400 `api-koppeling-expiry-out-of-range` unless `expiresAt` lies in the future and at most twelve months ahead; with 503 `api-koppelingen-unavailable` when Keycloak cannot be reached. Requires the `integrations.manage` permission. 
     * @summary Create an API-koppeling
     * @param {string} organizationId Organization ID
     * @param {CreateApiKoppelingRequest} createApiKoppelingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createApiKoppeling(organizationId: string, createApiKoppelingRequest: CreateApiKoppelingRequest, options?: RawAxiosRequestConfig) {
        return ApiKoppelingenApiFp(this.configuration).createApiKoppeling(organizationId, createApiKoppelingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The koppeling, without its secret. Requires the `integrations.manage` permission.
     * @summary Get one API-koppeling
     * @param {string} organizationId Organization ID
     * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getApiKoppeling(organizationId: string, koppelingId: string, options?: RawAxiosRequestConfig) {
        return ApiKoppelingenApiFp(this.configuration).getApiKoppeling(organizationId, koppelingId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Whether the organization has accepted the editions of the Overeenkomst and Bijlage A that cover koppelingen — the condition `createApiKoppeling` refuses with 409 `api-koppeling-terms-not-accepted`, answered before anyone fills in the form. Requires the `integrations.manage` permission. 
     * @summary Whether the organization may create API-koppelingen
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getApiKoppelingTerms(organizationId: string, options?: RawAxiosRequestConfig) {
        return ApiKoppelingenApiFp(this.configuration).getApiKoppelingTerms(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every API-koppeling of the organization, newest first, revoked and expired ones included (ADR-045). A secret is never returned here. Requires the `integrations.manage` permission. 
     * @summary List the organization\'s API-koppelingen
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listApiKoppelingen(organizationId: string, options?: RawAxiosRequestConfig) {
        return ApiKoppelingenApiFp(this.configuration).listApiKoppelingen(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Revokes the koppeling for good: its tokens are refused from this request on, and its Keycloak client is disabled so no secret obtains a new one. Revoking a revoked koppeling changes nothing. Requires the `integrations.manage` permission. 
     * @summary Revoke an API-koppeling
     * @param {string} organizationId Organization ID
     * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public revokeApiKoppeling(organizationId: string, koppelingId: string, options?: RawAxiosRequestConfig) {
        return ApiKoppelingenApiFp(this.configuration).revokeApiKoppeling(organizationId, koppelingId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Issues a new secret, valid until the new `expiresAt`, and returns it once. The previous secret keeps working for 24 hours, so the integration can switch over without an outage. An expired koppeling is renewed this way; a revoked one is refused with 409 `api-koppeling-revoked`. Requires the `integrations.manage` permission. 
     * @summary Rotate an API-koppeling\'s secret
     * @param {string} organizationId Organization ID
     * @param {string} koppelingId The API-koppeling (&#x60;api_koppelingen.id&#x60;)
     * @param {RotateApiKoppelingSecretRequest} rotateApiKoppelingSecretRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public rotateApiKoppelingSecret(organizationId: string, koppelingId: string, rotateApiKoppelingSecretRequest: RotateApiKoppelingSecretRequest, options?: RawAxiosRequestConfig) {
        return ApiKoppelingenApiFp(this.configuration).rotateApiKoppelingSecret(organizationId, koppelingId, rotateApiKoppelingSecretRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * AuthApi - axios parameter creator
 */
export const AuthApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Starts self-service password recovery for a behandelaar account on `app.scrivio.nl`. The backend generates a Firebase out-of-band password-reset link and delivers it with the branded Scrivio template, the same way the admin-portal-triggered reset does.  ## No account enumeration The response is `204 No Content` whether or not the address belongs to an account, and whether or not delivery succeeds. Nothing in the status, body, or timing distinguishes the two, so this endpoint cannot be used to discover which addresses are registered.  ## Scope Only behandelaar accounts (the `users` table) are eligible. Clients use the portal at `mijn.scrivio.nl`, which is passwordless (ADR-013) and has no password to reset — a portal address therefore silently produces no email, like any other unknown address.  ## Rate limiting Requests are throttled per email address so the endpoint cannot be used to mail-bomb an account holder. Exceeding the limit returns `429`. 
         * @summary Request a password-reset email
         * @param {PasswordResetRequest} passwordResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        requestPasswordReset: async (passwordResetRequest: PasswordResetRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'passwordResetRequest' is not null or undefined
            assertParamExists('requestPasswordReset', 'passwordResetRequest', passwordResetRequest)
            const localVarPath = `/api/v1/auth/password-reset`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(passwordResetRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * AuthApi - functional programming interface
 */
export const AuthApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = AuthApiAxiosParamCreator(configuration)
    return {
        /**
         * Starts self-service password recovery for a behandelaar account on `app.scrivio.nl`. The backend generates a Firebase out-of-band password-reset link and delivers it with the branded Scrivio template, the same way the admin-portal-triggered reset does.  ## No account enumeration The response is `204 No Content` whether or not the address belongs to an account, and whether or not delivery succeeds. Nothing in the status, body, or timing distinguishes the two, so this endpoint cannot be used to discover which addresses are registered.  ## Scope Only behandelaar accounts (the `users` table) are eligible. Clients use the portal at `mijn.scrivio.nl`, which is passwordless (ADR-013) and has no password to reset — a portal address therefore silently produces no email, like any other unknown address.  ## Rate limiting Requests are throttled per email address so the endpoint cannot be used to mail-bomb an account holder. Exceeding the limit returns `429`. 
         * @summary Request a password-reset email
         * @param {PasswordResetRequest} passwordResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async requestPasswordReset(passwordResetRequest: PasswordResetRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.requestPasswordReset(passwordResetRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['AuthApi.requestPasswordReset']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * AuthApi - factory interface
 */
export const AuthApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = AuthApiFp(configuration)
    return {
        /**
         * Starts self-service password recovery for a behandelaar account on `app.scrivio.nl`. The backend generates a Firebase out-of-band password-reset link and delivers it with the branded Scrivio template, the same way the admin-portal-triggered reset does.  ## No account enumeration The response is `204 No Content` whether or not the address belongs to an account, and whether or not delivery succeeds. Nothing in the status, body, or timing distinguishes the two, so this endpoint cannot be used to discover which addresses are registered.  ## Scope Only behandelaar accounts (the `users` table) are eligible. Clients use the portal at `mijn.scrivio.nl`, which is passwordless (ADR-013) and has no password to reset — a portal address therefore silently produces no email, like any other unknown address.  ## Rate limiting Requests are throttled per email address so the endpoint cannot be used to mail-bomb an account holder. Exceeding the limit returns `429`. 
         * @summary Request a password-reset email
         * @param {PasswordResetRequest} passwordResetRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        requestPasswordReset(passwordResetRequest: PasswordResetRequest, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.requestPasswordReset(passwordResetRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * AuthApi - object-oriented interface
 */
export class AuthApi extends BaseAPI {
    /**
     * Starts self-service password recovery for a behandelaar account on `app.scrivio.nl`. The backend generates a Firebase out-of-band password-reset link and delivers it with the branded Scrivio template, the same way the admin-portal-triggered reset does.  ## No account enumeration The response is `204 No Content` whether or not the address belongs to an account, and whether or not delivery succeeds. Nothing in the status, body, or timing distinguishes the two, so this endpoint cannot be used to discover which addresses are registered.  ## Scope Only behandelaar accounts (the `users` table) are eligible. Clients use the portal at `mijn.scrivio.nl`, which is passwordless (ADR-013) and has no password to reset — a portal address therefore silently produces no email, like any other unknown address.  ## Rate limiting Requests are throttled per email address so the endpoint cannot be used to mail-bomb an account holder. Exceeding the limit returns `429`. 
     * @summary Request a password-reset email
     * @param {PasswordResetRequest} passwordResetRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public requestPasswordReset(passwordResetRequest: PasswordResetRequest, options?: RawAxiosRequestConfig) {
        return AuthApiFp(this.configuration).requestPasswordReset(passwordResetRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * IntegrationsApi - axios parameter creator
 */
export const IntegrationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Clears the stored Zivver configuration and deletes the SMTP password from Secret Manager. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Delete the organization\'s Zivver SMTP configuration
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteZivverConfig: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('deleteZivverConfig', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zivver`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes the stored Account-ID, after which no letter can be sent to a huisarts on this practice\'s behalf. A stored mailbox login is left in place. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Clear the practice\'s ZorgMail Account-ID
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteZorgMailConfig: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('deleteZorgMailConfig', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zorgmail`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes the stored mailbox login and deletes its password from Secret Manager. The Account-ID is left untouched. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Remove the practice\'s ZorgMail Secure e-mail mailbox login
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteZorgMailMailbox: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('deleteZorgMailMailbox', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zorgmail/mailbox`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reports whether this practice has confirmed it arranged its own VECOZO connection for COV — the aansluitovereenkomst with VECOZO plus the toestemmingsverklaring authorizing Scrivio to query on behalf of its AGB-code (ADR-031 Decision 1) — together with the AGB-code that toestemmingsverklaring has to name.  This is the practice\'s own declaration, not a copy of VECOZO\'s answer: VECOZO remains the source of truth for whether an AGB is authorized, and still refuses an unauthorized call at request time. Requires the `integrations.manage` permission. 
         * @summary Get the organization\'s VECOZO COV connection prerequisites
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVecozoCovAgreement: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getVecozoCovAgreement', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/vecozo-cov`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the organization\'s Zivver secure-email SMTP configuration. The SMTP password is never returned; instead a `configured` flag indicates whether credentials have been stored. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Get the organization\'s Zivver SMTP configuration
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZivverConfig: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getZivverConfig', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zivver`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reports what the practice needs in order to switch the ZorgDomein receiving connection on itself (ADR-040 section 6): the identifiers ZorgDomein can search Scrivio\'s FHIR server on, the stable identifier Scrivio answers that search with, and — once ZorgDomein have delivered a `ZD Activation Verification` document — the verification code the practice types back into ZorgDomein to complete activation.  The verification code is short-lived and only meaningful to the person activating, so it is read here and is deliberately never mailed. Requires the `integrations.zorgdomein_activation.read` permission, which is separate from `integrations.manage`: seeing the code is seeing what completes a live clinical intake channel. 
         * @summary Get the organization\'s ZorgDomein auto-activation state
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgDomeinActivation: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getZorgDomeinActivation', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zorgdomein`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the practice\'s own ZorgMail Account-ID — the nine-digit id that becomes the SMTP `MAIL FROM` local part when Scrivio sends a MEDVRI on its behalf (#4121, ADR-041 §3).  The Account-ID is not a credential: one ASP account authenticates every EDI send for every customer. Beside it the response reports whether the practice\'s Secure e-mail mailbox login is stored (ADR-050 §4) and its username and host — never its password.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Get the organization\'s ZorgMail configuration
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgMailConfig: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getZorgMailConfig', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zorgmail`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Opens an authenticated, STARTTLS-secured SMTP session to the Zivver gateway using the stored credentials and immediately closes it without sending a message (AUTH then QUIT). Returns a structured success or failure result. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Test the organization\'s Zivver SMTP connection
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testZivverConnection: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('testZivverConnection', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zivver/test`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Logs in to the stored mailbox over IMAPS (implicit TLS on 993, certificate verified), lists its folders and logs out. Nothing in the mailbox is read or changed. Returns the outcome; a failed login is a `200` with a failure outcome, not an error.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Test the practice\'s ZorgMail Secure e-mail mailbox login
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testZorgMailMailbox: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('testZorgMailMailbox', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zorgmail/mailbox/test`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Records that the practice arranged its VECOZO COV aansluiting and toestemmingsverklaring, or withdraws that confirmation. While it is not confirmed, Scrivio does not offer COV verification and refuses a verification call with `VECOZO_AGREEMENT_MISSING` rather than sending a request VECOZO will reject for a contractual reason.  Re-confirming an already-confirmed agreement leaves the recorded moment and actor unchanged. Requires the `integrations.manage` permission. 
         * @summary Record or withdraw the organization\'s VECOZO COV agreement confirmation
         * @param {string} organizationId Organization ID
         * @param {VecozoCovAgreementUpdateRequest} vecozoCovAgreementUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateVecozoCovAgreement: async (organizationId: string, vecozoCovAgreementUpdateRequest: VecozoCovAgreementUpdateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('updateVecozoCovAgreement', 'organizationId', organizationId)
            // verify required parameter 'vecozoCovAgreementUpdateRequest' is not null or undefined
            assertParamExists('updateVecozoCovAgreement', 'vecozoCovAgreementUpdateRequest', vecozoCovAgreementUpdateRequest)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/vecozo-cov`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(vecozoCovAgreementUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stores the Zivver SMTP host, port, username and password. The password is written to Secret Manager and never persisted in the database or returned by any endpoint. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Create or update the organization\'s Zivver SMTP configuration
         * @param {string} organizationId Organization ID
         * @param {ZivverConfigUpdateRequest} zivverConfigUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateZivverConfig: async (organizationId: string, zivverConfigUpdateRequest: ZivverConfigUpdateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('updateZivverConfig', 'organizationId', organizationId)
            // verify required parameter 'zivverConfigUpdateRequest' is not null or undefined
            assertParamExists('updateZivverConfig', 'zivverConfigUpdateRequest', zivverConfigUpdateRequest)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zivver`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(zivverConfigUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stores the ZorgMail Account-ID Enovation issued to this practice.  **Recording it is only half the prerequisite.** Enovation also record, per subscriber, whether Scrivio may send on their behalf; where they have not, the first send is refused at `MAIL FROM` and reported as `zorgmail-send-refused` with refusal `NOT_PERMITTED_FOR_SENDER`. Nothing here can discover or anticipate that.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Record the practice\'s ZorgMail Account-ID
         * @param {string} organizationId Organization ID
         * @param {ZorgMailConfigUpdateRequest} zorgMailConfigUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateZorgMailConfig: async (organizationId: string, zorgMailConfigUpdateRequest: ZorgMailConfigUpdateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('updateZorgMailConfig', 'organizationId', organizationId)
            // verify required parameter 'zorgMailConfigUpdateRequest' is not null or undefined
            assertParamExists('updateZorgMailConfig', 'zorgMailConfigUpdateRequest', zorgMailConfigUpdateRequest)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zorgmail`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(zorgMailConfigUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stores the username and password of the practice\'s own ZorgMail Secure e-mail mailbox, which the inbox reads with (ADR-050 §4). The password is written to Secret Manager and is never returned. Replaces any login stored before; the Account-ID is left untouched.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Store the practice\'s ZorgMail Secure e-mail mailbox login
         * @param {string} organizationId Organization ID
         * @param {ZorgMailMailboxUpdateRequest} zorgMailMailboxUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateZorgMailMailbox: async (organizationId: string, zorgMailMailboxUpdateRequest: ZorgMailMailboxUpdateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('updateZorgMailMailbox', 'organizationId', organizationId)
            // verify required parameter 'zorgMailMailboxUpdateRequest' is not null or undefined
            assertParamExists('updateZorgMailMailbox', 'zorgMailMailboxUpdateRequest', zorgMailMailboxUpdateRequest)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/zorgmail/mailbox`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(zorgMailMailboxUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * IntegrationsApi - functional programming interface
 */
export const IntegrationsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = IntegrationsApiAxiosParamCreator(configuration)
    return {
        /**
         * Clears the stored Zivver configuration and deletes the SMTP password from Secret Manager. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Delete the organization\'s Zivver SMTP configuration
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteZivverConfig(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteZivverConfig(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.deleteZivverConfig']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes the stored Account-ID, after which no letter can be sent to a huisarts on this practice\'s behalf. A stored mailbox login is left in place. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Clear the practice\'s ZorgMail Account-ID
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteZorgMailConfig(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteZorgMailConfig(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.deleteZorgMailConfig']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes the stored mailbox login and deletes its password from Secret Manager. The Account-ID is left untouched. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Remove the practice\'s ZorgMail Secure e-mail mailbox login
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteZorgMailMailbox(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteZorgMailMailbox(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.deleteZorgMailMailbox']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reports whether this practice has confirmed it arranged its own VECOZO connection for COV — the aansluitovereenkomst with VECOZO plus the toestemmingsverklaring authorizing Scrivio to query on behalf of its AGB-code (ADR-031 Decision 1) — together with the AGB-code that toestemmingsverklaring has to name.  This is the practice\'s own declaration, not a copy of VECOZO\'s answer: VECOZO remains the source of truth for whether an AGB is authorized, and still refuses an unauthorized call at request time. Requires the `integrations.manage` permission. 
         * @summary Get the organization\'s VECOZO COV connection prerequisites
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getVecozoCovAgreement(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VecozoCovAgreementResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getVecozoCovAgreement(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.getVecozoCovAgreement']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the organization\'s Zivver secure-email SMTP configuration. The SMTP password is never returned; instead a `configured` flag indicates whether credentials have been stored. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Get the organization\'s Zivver SMTP configuration
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getZivverConfig(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZivverConfigResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getZivverConfig(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.getZivverConfig']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reports what the practice needs in order to switch the ZorgDomein receiving connection on itself (ADR-040 section 6): the identifiers ZorgDomein can search Scrivio\'s FHIR server on, the stable identifier Scrivio answers that search with, and — once ZorgDomein have delivered a `ZD Activation Verification` document — the verification code the practice types back into ZorgDomein to complete activation.  The verification code is short-lived and only meaningful to the person activating, so it is read here and is deliberately never mailed. Requires the `integrations.zorgdomein_activation.read` permission, which is separate from `integrations.manage`: seeing the code is seeing what completes a live clinical intake channel. 
         * @summary Get the organization\'s ZorgDomein auto-activation state
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getZorgDomeinActivation(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgDomeinActivationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getZorgDomeinActivation(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.getZorgDomeinActivation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the practice\'s own ZorgMail Account-ID — the nine-digit id that becomes the SMTP `MAIL FROM` local part when Scrivio sends a MEDVRI on its behalf (#4121, ADR-041 §3).  The Account-ID is not a credential: one ASP account authenticates every EDI send for every customer. Beside it the response reports whether the practice\'s Secure e-mail mailbox login is stored (ADR-050 §4) and its username and host — never its password.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Get the organization\'s ZorgMail configuration
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getZorgMailConfig(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgMailConfigResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getZorgMailConfig(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.getZorgMailConfig']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Opens an authenticated, STARTTLS-secured SMTP session to the Zivver gateway using the stored credentials and immediately closes it without sending a message (AUTH then QUIT). Returns a structured success or failure result. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Test the organization\'s Zivver SMTP connection
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async testZivverConnection(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZivverTestResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.testZivverConnection(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.testZivverConnection']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Logs in to the stored mailbox over IMAPS (implicit TLS on 993, certificate verified), lists its folders and logs out. Nothing in the mailbox is read or changed. Returns the outcome; a failed login is a `200` with a failure outcome, not an error.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Test the practice\'s ZorgMail Secure e-mail mailbox login
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async testZorgMailMailbox(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgMailMailboxTestResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.testZorgMailMailbox(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.testZorgMailMailbox']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Records that the practice arranged its VECOZO COV aansluiting and toestemmingsverklaring, or withdraws that confirmation. While it is not confirmed, Scrivio does not offer COV verification and refuses a verification call with `VECOZO_AGREEMENT_MISSING` rather than sending a request VECOZO will reject for a contractual reason.  Re-confirming an already-confirmed agreement leaves the recorded moment and actor unchanged. Requires the `integrations.manage` permission. 
         * @summary Record or withdraw the organization\'s VECOZO COV agreement confirmation
         * @param {string} organizationId Organization ID
         * @param {VecozoCovAgreementUpdateRequest} vecozoCovAgreementUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateVecozoCovAgreement(organizationId: string, vecozoCovAgreementUpdateRequest: VecozoCovAgreementUpdateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VecozoCovAgreementResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateVecozoCovAgreement(organizationId, vecozoCovAgreementUpdateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.updateVecozoCovAgreement']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stores the Zivver SMTP host, port, username and password. The password is written to Secret Manager and never persisted in the database or returned by any endpoint. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Create or update the organization\'s Zivver SMTP configuration
         * @param {string} organizationId Organization ID
         * @param {ZivverConfigUpdateRequest} zivverConfigUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateZivverConfig(organizationId: string, zivverConfigUpdateRequest: ZivverConfigUpdateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZivverConfigResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateZivverConfig(organizationId, zivverConfigUpdateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.updateZivverConfig']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stores the ZorgMail Account-ID Enovation issued to this practice.  **Recording it is only half the prerequisite.** Enovation also record, per subscriber, whether Scrivio may send on their behalf; where they have not, the first send is refused at `MAIL FROM` and reported as `zorgmail-send-refused` with refusal `NOT_PERMITTED_FOR_SENDER`. Nothing here can discover or anticipate that.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Record the practice\'s ZorgMail Account-ID
         * @param {string} organizationId Organization ID
         * @param {ZorgMailConfigUpdateRequest} zorgMailConfigUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateZorgMailConfig(organizationId: string, zorgMailConfigUpdateRequest: ZorgMailConfigUpdateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgMailConfigResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateZorgMailConfig(organizationId, zorgMailConfigUpdateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.updateZorgMailConfig']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stores the username and password of the practice\'s own ZorgMail Secure e-mail mailbox, which the inbox reads with (ADR-050 §4). The password is written to Secret Manager and is never returned. Replaces any login stored before; the Account-ID is left untouched.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Store the practice\'s ZorgMail Secure e-mail mailbox login
         * @param {string} organizationId Organization ID
         * @param {ZorgMailMailboxUpdateRequest} zorgMailMailboxUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateZorgMailMailbox(organizationId: string, zorgMailMailboxUpdateRequest: ZorgMailMailboxUpdateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgMailConfigResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateZorgMailMailbox(organizationId, zorgMailMailboxUpdateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['IntegrationsApi.updateZorgMailMailbox']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * IntegrationsApi - factory interface
 */
export const IntegrationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = IntegrationsApiFp(configuration)
    return {
        /**
         * Clears the stored Zivver configuration and deletes the SMTP password from Secret Manager. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Delete the organization\'s Zivver SMTP configuration
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteZivverConfig(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteZivverConfig(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes the stored Account-ID, after which no letter can be sent to a huisarts on this practice\'s behalf. A stored mailbox login is left in place. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Clear the practice\'s ZorgMail Account-ID
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteZorgMailConfig(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteZorgMailConfig(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes the stored mailbox login and deletes its password from Secret Manager. The Account-ID is left untouched. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Remove the practice\'s ZorgMail Secure e-mail mailbox login
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteZorgMailMailbox(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteZorgMailMailbox(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Reports whether this practice has confirmed it arranged its own VECOZO connection for COV — the aansluitovereenkomst with VECOZO plus the toestemmingsverklaring authorizing Scrivio to query on behalf of its AGB-code (ADR-031 Decision 1) — together with the AGB-code that toestemmingsverklaring has to name.  This is the practice\'s own declaration, not a copy of VECOZO\'s answer: VECOZO remains the source of truth for whether an AGB is authorized, and still refuses an unauthorized call at request time. Requires the `integrations.manage` permission. 
         * @summary Get the organization\'s VECOZO COV connection prerequisites
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getVecozoCovAgreement(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<VecozoCovAgreementResponse> {
            return localVarFp.getVecozoCovAgreement(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the organization\'s Zivver secure-email SMTP configuration. The SMTP password is never returned; instead a `configured` flag indicates whether credentials have been stored. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Get the organization\'s Zivver SMTP configuration
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZivverConfig(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<ZivverConfigResponse> {
            return localVarFp.getZivverConfig(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Reports what the practice needs in order to switch the ZorgDomein receiving connection on itself (ADR-040 section 6): the identifiers ZorgDomein can search Scrivio\'s FHIR server on, the stable identifier Scrivio answers that search with, and — once ZorgDomein have delivered a `ZD Activation Verification` document — the verification code the practice types back into ZorgDomein to complete activation.  The verification code is short-lived and only meaningful to the person activating, so it is read here and is deliberately never mailed. Requires the `integrations.zorgdomein_activation.read` permission, which is separate from `integrations.manage`: seeing the code is seeing what completes a live clinical intake channel. 
         * @summary Get the organization\'s ZorgDomein auto-activation state
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgDomeinActivation(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<ZorgDomeinActivationResponse> {
            return localVarFp.getZorgDomeinActivation(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the practice\'s own ZorgMail Account-ID — the nine-digit id that becomes the SMTP `MAIL FROM` local part when Scrivio sends a MEDVRI on its behalf (#4121, ADR-041 §3).  The Account-ID is not a credential: one ASP account authenticates every EDI send for every customer. Beside it the response reports whether the practice\'s Secure e-mail mailbox login is stored (ADR-050 §4) and its username and host — never its password.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Get the organization\'s ZorgMail configuration
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgMailConfig(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<ZorgMailConfigResponse> {
            return localVarFp.getZorgMailConfig(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Opens an authenticated, STARTTLS-secured SMTP session to the Zivver gateway using the stored credentials and immediately closes it without sending a message (AUTH then QUIT). Returns a structured success or failure result. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Test the organization\'s Zivver SMTP connection
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testZivverConnection(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<ZivverTestResult> {
            return localVarFp.testZivverConnection(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Logs in to the stored mailbox over IMAPS (implicit TLS on 993, certificate verified), lists its folders and logs out. Nothing in the mailbox is read or changed. Returns the outcome; a failed login is a `200` with a failure outcome, not an error.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Test the practice\'s ZorgMail Secure e-mail mailbox login
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testZorgMailMailbox(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<ZorgMailMailboxTestResult> {
            return localVarFp.testZorgMailMailbox(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Records that the practice arranged its VECOZO COV aansluiting and toestemmingsverklaring, or withdraws that confirmation. While it is not confirmed, Scrivio does not offer COV verification and refuses a verification call with `VECOZO_AGREEMENT_MISSING` rather than sending a request VECOZO will reject for a contractual reason.  Re-confirming an already-confirmed agreement leaves the recorded moment and actor unchanged. Requires the `integrations.manage` permission. 
         * @summary Record or withdraw the organization\'s VECOZO COV agreement confirmation
         * @param {string} organizationId Organization ID
         * @param {VecozoCovAgreementUpdateRequest} vecozoCovAgreementUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateVecozoCovAgreement(organizationId: string, vecozoCovAgreementUpdateRequest: VecozoCovAgreementUpdateRequest, options?: RawAxiosRequestConfig): AxiosPromise<VecozoCovAgreementResponse> {
            return localVarFp.updateVecozoCovAgreement(organizationId, vecozoCovAgreementUpdateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Stores the Zivver SMTP host, port, username and password. The password is written to Secret Manager and never persisted in the database or returned by any endpoint. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
         * @summary Create or update the organization\'s Zivver SMTP configuration
         * @param {string} organizationId Organization ID
         * @param {ZivverConfigUpdateRequest} zivverConfigUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateZivverConfig(organizationId: string, zivverConfigUpdateRequest: ZivverConfigUpdateRequest, options?: RawAxiosRequestConfig): AxiosPromise<ZivverConfigResponse> {
            return localVarFp.updateZivverConfig(organizationId, zivverConfigUpdateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Stores the ZorgMail Account-ID Enovation issued to this practice.  **Recording it is only half the prerequisite.** Enovation also record, per subscriber, whether Scrivio may send on their behalf; where they have not, the first send is refused at `MAIL FROM` and reported as `zorgmail-send-refused` with refusal `NOT_PERMITTED_FOR_SENDER`. Nothing here can discover or anticipate that.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Record the practice\'s ZorgMail Account-ID
         * @param {string} organizationId Organization ID
         * @param {ZorgMailConfigUpdateRequest} zorgMailConfigUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateZorgMailConfig(organizationId: string, zorgMailConfigUpdateRequest: ZorgMailConfigUpdateRequest, options?: RawAxiosRequestConfig): AxiosPromise<ZorgMailConfigResponse> {
            return localVarFp.updateZorgMailConfig(organizationId, zorgMailConfigUpdateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Stores the username and password of the practice\'s own ZorgMail Secure e-mail mailbox, which the inbox reads with (ADR-050 §4). The password is written to Secret Manager and is never returned. Replaces any login stored before; the Account-ID is left untouched.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Store the practice\'s ZorgMail Secure e-mail mailbox login
         * @param {string} organizationId Organization ID
         * @param {ZorgMailMailboxUpdateRequest} zorgMailMailboxUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateZorgMailMailbox(organizationId: string, zorgMailMailboxUpdateRequest: ZorgMailMailboxUpdateRequest, options?: RawAxiosRequestConfig): AxiosPromise<ZorgMailConfigResponse> {
            return localVarFp.updateZorgMailMailbox(organizationId, zorgMailMailboxUpdateRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * IntegrationsApi - object-oriented interface
 */
export class IntegrationsApi extends BaseAPI {
    /**
     * Clears the stored Zivver configuration and deletes the SMTP password from Secret Manager. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
     * @summary Delete the organization\'s Zivver SMTP configuration
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteZivverConfig(organizationId: string, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).deleteZivverConfig(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes the stored Account-ID, after which no letter can be sent to a huisarts on this practice\'s behalf. A stored mailbox login is left in place. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Clear the practice\'s ZorgMail Account-ID
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteZorgMailConfig(organizationId: string, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).deleteZorgMailConfig(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes the stored mailbox login and deletes its password from Secret Manager. The Account-ID is left untouched. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Remove the practice\'s ZorgMail Secure e-mail mailbox login
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteZorgMailMailbox(organizationId: string, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).deleteZorgMailMailbox(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reports whether this practice has confirmed it arranged its own VECOZO connection for COV — the aansluitovereenkomst with VECOZO plus the toestemmingsverklaring authorizing Scrivio to query on behalf of its AGB-code (ADR-031 Decision 1) — together with the AGB-code that toestemmingsverklaring has to name.  This is the practice\'s own declaration, not a copy of VECOZO\'s answer: VECOZO remains the source of truth for whether an AGB is authorized, and still refuses an unauthorized call at request time. Requires the `integrations.manage` permission. 
     * @summary Get the organization\'s VECOZO COV connection prerequisites
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getVecozoCovAgreement(organizationId: string, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).getVecozoCovAgreement(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the organization\'s Zivver secure-email SMTP configuration. The SMTP password is never returned; instead a `configured` flag indicates whether credentials have been stored. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
     * @summary Get the organization\'s Zivver SMTP configuration
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getZivverConfig(organizationId: string, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).getZivverConfig(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reports what the practice needs in order to switch the ZorgDomein receiving connection on itself (ADR-040 section 6): the identifiers ZorgDomein can search Scrivio\'s FHIR server on, the stable identifier Scrivio answers that search with, and — once ZorgDomein have delivered a `ZD Activation Verification` document — the verification code the practice types back into ZorgDomein to complete activation.  The verification code is short-lived and only meaningful to the person activating, so it is read here and is deliberately never mailed. Requires the `integrations.zorgdomein_activation.read` permission, which is separate from `integrations.manage`: seeing the code is seeing what completes a live clinical intake channel. 
     * @summary Get the organization\'s ZorgDomein auto-activation state
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getZorgDomeinActivation(organizationId: string, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).getZorgDomeinActivation(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the practice\'s own ZorgMail Account-ID — the nine-digit id that becomes the SMTP `MAIL FROM` local part when Scrivio sends a MEDVRI on its behalf (#4121, ADR-041 §3).  The Account-ID is not a credential: one ASP account authenticates every EDI send for every customer. Beside it the response reports whether the practice\'s Secure e-mail mailbox login is stored (ADR-050 §4) and its username and host — never its password.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Get the organization\'s ZorgMail configuration
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getZorgMailConfig(organizationId: string, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).getZorgMailConfig(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Opens an authenticated, STARTTLS-secured SMTP session to the Zivver gateway using the stored credentials and immediately closes it without sending a message (AUTH then QUIT). Returns a structured success or failure result. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
     * @summary Test the organization\'s Zivver SMTP connection
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public testZivverConnection(organizationId: string, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).testZivverConnection(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Logs in to the stored mailbox over IMAPS (implicit TLS on 993, certificate verified), lists its folders and logs out. Nothing in the mailbox is read or changed. Returns the outcome; a failed login is a `200` with a failure outcome, not an error.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Test the practice\'s ZorgMail Secure e-mail mailbox login
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public testZorgMailMailbox(organizationId: string, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).testZorgMailMailbox(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Records that the practice arranged its VECOZO COV aansluiting and toestemmingsverklaring, or withdraws that confirmation. While it is not confirmed, Scrivio does not offer COV verification and refuses a verification call with `VECOZO_AGREEMENT_MISSING` rather than sending a request VECOZO will reject for a contractual reason.  Re-confirming an already-confirmed agreement leaves the recorded moment and actor unchanged. Requires the `integrations.manage` permission. 
     * @summary Record or withdraw the organization\'s VECOZO COV agreement confirmation
     * @param {string} organizationId Organization ID
     * @param {VecozoCovAgreementUpdateRequest} vecozoCovAgreementUpdateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateVecozoCovAgreement(organizationId: string, vecozoCovAgreementUpdateRequest: VecozoCovAgreementUpdateRequest, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).updateVecozoCovAgreement(organizationId, vecozoCovAgreementUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stores the Zivver SMTP host, port, username and password. The password is written to Secret Manager and never persisted in the database or returned by any endpoint. Requires the `integrations.manage` permission and a FULL license (ADR-023, ADR-028). 
     * @summary Create or update the organization\'s Zivver SMTP configuration
     * @param {string} organizationId Organization ID
     * @param {ZivverConfigUpdateRequest} zivverConfigUpdateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateZivverConfig(organizationId: string, zivverConfigUpdateRequest: ZivverConfigUpdateRequest, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).updateZivverConfig(organizationId, zivverConfigUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stores the ZorgMail Account-ID Enovation issued to this practice.  **Recording it is only half the prerequisite.** Enovation also record, per subscriber, whether Scrivio may send on their behalf; where they have not, the first send is refused at `MAIL FROM` and reported as `zorgmail-send-refused` with refusal `NOT_PERMITTED_FOR_SENDER`. Nothing here can discover or anticipate that.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Record the practice\'s ZorgMail Account-ID
     * @param {string} organizationId Organization ID
     * @param {ZorgMailConfigUpdateRequest} zorgMailConfigUpdateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateZorgMailConfig(organizationId: string, zorgMailConfigUpdateRequest: ZorgMailConfigUpdateRequest, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).updateZorgMailConfig(organizationId, zorgMailConfigUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stores the username and password of the practice\'s own ZorgMail Secure e-mail mailbox, which the inbox reads with (ADR-050 §4). The password is written to Secret Manager and is never returned. Replaces any login stored before; the Account-ID is left untouched.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Store the practice\'s ZorgMail Secure e-mail mailbox login
     * @param {string} organizationId Organization ID
     * @param {ZorgMailMailboxUpdateRequest} zorgMailMailboxUpdateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateZorgMailMailbox(organizationId: string, zorgMailMailboxUpdateRequest: ZorgMailMailboxUpdateRequest, options?: RawAxiosRequestConfig) {
        return IntegrationsApiFp(this.configuration).updateZorgMailMailbox(organizationId, zorgMailMailboxUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * NawApi - axios parameter creator
 */
export const NawApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Looks up name, address, and residence (NAW) data for a person by their BSN (Burgerservicenummer) via the SBV-Z (Sectorale Berichten Voorziening in de Zorg) integration.  This endpoint is used when registering new clients to auto-fill address information.  ## Rate Limiting Maximum 10 lookups per minute per organization to prevent abuse.  ## Prerequisites The organization must have a valid UZI certificate configured in order to communicate with SBV-Z. 
         * @summary Look up NAW data by BSN
         * @param {NawLookupRequest} nawLookupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        lookupNaw: async (nawLookupRequest: NawLookupRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'nawLookupRequest' is not null or undefined
            assertParamExists('lookupNaw', 'nawLookupRequest', nawLookupRequest)
            const localVarPath = `/api/v1/naw/lookup`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(nawLookupRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * NawApi - functional programming interface
 */
export const NawApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = NawApiAxiosParamCreator(configuration)
    return {
        /**
         * Looks up name, address, and residence (NAW) data for a person by their BSN (Burgerservicenummer) via the SBV-Z (Sectorale Berichten Voorziening in de Zorg) integration.  This endpoint is used when registering new clients to auto-fill address information.  ## Rate Limiting Maximum 10 lookups per minute per organization to prevent abuse.  ## Prerequisites The organization must have a valid UZI certificate configured in order to communicate with SBV-Z. 
         * @summary Look up NAW data by BSN
         * @param {NawLookupRequest} nawLookupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async lookupNaw(nawLookupRequest: NawLookupRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<NawLookupResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.lookupNaw(nawLookupRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['NawApi.lookupNaw']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * NawApi - factory interface
 */
export const NawApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = NawApiFp(configuration)
    return {
        /**
         * Looks up name, address, and residence (NAW) data for a person by their BSN (Burgerservicenummer) via the SBV-Z (Sectorale Berichten Voorziening in de Zorg) integration.  This endpoint is used when registering new clients to auto-fill address information.  ## Rate Limiting Maximum 10 lookups per minute per organization to prevent abuse.  ## Prerequisites The organization must have a valid UZI certificate configured in order to communicate with SBV-Z. 
         * @summary Look up NAW data by BSN
         * @param {NawLookupRequest} nawLookupRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        lookupNaw(nawLookupRequest: NawLookupRequest, options?: RawAxiosRequestConfig): AxiosPromise<NawLookupResponse> {
            return localVarFp.lookupNaw(nawLookupRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * NawApi - object-oriented interface
 */
export class NawApi extends BaseAPI {
    /**
     * Looks up name, address, and residence (NAW) data for a person by their BSN (Burgerservicenummer) via the SBV-Z (Sectorale Berichten Voorziening in de Zorg) integration.  This endpoint is used when registering new clients to auto-fill address information.  ## Rate Limiting Maximum 10 lookups per minute per organization to prevent abuse.  ## Prerequisites The organization must have a valid UZI certificate configured in order to communicate with SBV-Z. 
     * @summary Look up NAW data by BSN
     * @param {NawLookupRequest} nawLookupRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public lookupNaw(nawLookupRequest: NawLookupRequest, options?: RawAxiosRequestConfig) {
        return NawApiFp(this.configuration).lookupNaw(nawLookupRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * SignInApi - axios parameter creator
 */
export const SignInApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * The first step of identifier-first sign-in (ADR-045 §7): the sign-in page asks for the e-mailadres, then sends the person to the provider this returns.  - `APP` — `KEYCLOAK` when the address belongs to a live behandelaar account with an   enabled membership of at least one organisation switched to Keycloak, else `FIREBASE`. - `ADMIN` — `KEYCLOAK` when the address belongs to a live admin user switched to Keycloak,   else `FIREBASE`.  ## No account enumeration An unknown address is answered `FIREBASE`, exactly like a known one on Firebase, in the same response shape, and from the same single query — so the answer cannot tell an unknown address from a known one. Only an address already moved to Keycloak answers differently, which identifier-first sign-in cannot avoid: it is the answer. 
         * @summary Which provider an e-mailadres signs in with
         * @param {SignInMethodRequest} signInMethodRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveSignInMethod: async (signInMethodRequest: SignInMethodRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'signInMethodRequest' is not null or undefined
            assertParamExists('resolveSignInMethod', 'signInMethodRequest', signInMethodRequest)
            const localVarPath = `/api/v1/auth/sign-in-method`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(signInMethodRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SignInApi - functional programming interface
 */
export const SignInApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SignInApiAxiosParamCreator(configuration)
    return {
        /**
         * The first step of identifier-first sign-in (ADR-045 §7): the sign-in page asks for the e-mailadres, then sends the person to the provider this returns.  - `APP` — `KEYCLOAK` when the address belongs to a live behandelaar account with an   enabled membership of at least one organisation switched to Keycloak, else `FIREBASE`. - `ADMIN` — `KEYCLOAK` when the address belongs to a live admin user switched to Keycloak,   else `FIREBASE`.  ## No account enumeration An unknown address is answered `FIREBASE`, exactly like a known one on Firebase, in the same response shape, and from the same single query — so the answer cannot tell an unknown address from a known one. Only an address already moved to Keycloak answers differently, which identifier-first sign-in cannot avoid: it is the answer. 
         * @summary Which provider an e-mailadres signs in with
         * @param {SignInMethodRequest} signInMethodRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async resolveSignInMethod(signInMethodRequest: SignInMethodRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SignInMethodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.resolveSignInMethod(signInMethodRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SignInApi.resolveSignInMethod']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SignInApi - factory interface
 */
export const SignInApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SignInApiFp(configuration)
    return {
        /**
         * The first step of identifier-first sign-in (ADR-045 §7): the sign-in page asks for the e-mailadres, then sends the person to the provider this returns.  - `APP` — `KEYCLOAK` when the address belongs to a live behandelaar account with an   enabled membership of at least one organisation switched to Keycloak, else `FIREBASE`. - `ADMIN` — `KEYCLOAK` when the address belongs to a live admin user switched to Keycloak,   else `FIREBASE`.  ## No account enumeration An unknown address is answered `FIREBASE`, exactly like a known one on Firebase, in the same response shape, and from the same single query — so the answer cannot tell an unknown address from a known one. Only an address already moved to Keycloak answers differently, which identifier-first sign-in cannot avoid: it is the answer. 
         * @summary Which provider an e-mailadres signs in with
         * @param {SignInMethodRequest} signInMethodRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resolveSignInMethod(signInMethodRequest: SignInMethodRequest, options?: RawAxiosRequestConfig): AxiosPromise<SignInMethodResponse> {
            return localVarFp.resolveSignInMethod(signInMethodRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SignInApi - object-oriented interface
 */
export class SignInApi extends BaseAPI {
    /**
     * The first step of identifier-first sign-in (ADR-045 §7): the sign-in page asks for the e-mailadres, then sends the person to the provider this returns.  - `APP` — `KEYCLOAK` when the address belongs to a live behandelaar account with an   enabled membership of at least one organisation switched to Keycloak, else `FIREBASE`. - `ADMIN` — `KEYCLOAK` when the address belongs to a live admin user switched to Keycloak,   else `FIREBASE`.  ## No account enumeration An unknown address is answered `FIREBASE`, exactly like a known one on Firebase, in the same response shape, and from the same single query — so the answer cannot tell an unknown address from a known one. Only an address already moved to Keycloak answers differently, which identifier-first sign-in cannot avoid: it is the answer. 
     * @summary Which provider an e-mailadres signs in with
     * @param {SignInMethodRequest} signInMethodRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public resolveSignInMethod(signInMethodRequest: SignInMethodRequest, options?: RawAxiosRequestConfig) {
        return SignInApiFp(this.configuration).resolveSignInMethod(signInMethodRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * SnelstartIntegrationApi - axios parameter creator
 */
export const SnelstartIntegrationApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Records the grootboekrekening with SnelStart\'s `id`, which is what a verkoopboeking references. The nummer and omschrijving stored beside it are read back from SnelStart, for display only. An id that is not an active account in the administratie is a `400`; a connection that cannot be read is a `409`. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Choose the grootboekrekening for verzekeraarfacturen
         * @param {string} organizationId Organization ID
         * @param {SnelStartGrootboekChoiceRequest} snelStartGrootboekChoiceRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        chooseSnelStartGrootboek: async (organizationId: string, snelStartGrootboekChoiceRequest: SnelStartGrootboekChoiceRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('chooseSnelStartGrootboek', 'organizationId', organizationId)
            // verify required parameter 'snelStartGrootboekChoiceRequest' is not null or undefined
            assertParamExists('chooseSnelStartGrootboek', 'snelStartGrootboekChoiceRequest', snelStartGrootboekChoiceRequest)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart/grootboek`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(snelStartGrootboekChoiceRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * SnelStart\'s webhook carries no signature, so a key it delivered waits for a person to recognise the administratie it belongs to (`administratieNaam`) before anything is booked with it (#4758). A `409` when nothing awaits confirmation. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Confirm the administratie a productiekoppeling connected to
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        confirmSnelStartAdministratie: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('confirmSnelStartAdministratie', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart/confirm`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Stores a koppelingssleutel (maatwerksleutel) the practice generated in SnelStart under Koppelingen → Maatwerk, replacing any stored one. The key is written to Secret Manager and never persisted in the database or returned.  A connection made this way is a `MAATWERK` connection, which sends the subscription key a Scrivio platform admin stored for this organisation. The productiekoppeling (#4758) stores its key through the same path with route `PRODUCTIE`.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Connect the organization to SnelStart with a koppelingssleutel
         * @param {string} organizationId Organization ID
         * @param {SnelStartConnectRequest} snelStartConnectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        connectSnelStart: async (organizationId: string, snelStartConnectRequest: SnelStartConnectRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('connectSnelStart', 'organizationId', organizationId)
            // verify required parameter 'snelStartConnectRequest' is not null or undefined
            assertParamExists('connectSnelStart', 'snelStartConnectRequest', snelStartConnectRequest)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(snelStartConnectRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deletes the koppelingssleutel from Secret Manager and forgets the grootboekrekening chosen in that administratie. Idempotent. The maatwerk subscription key is Scrivio\'s and stays; a platform admin removes it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Disconnect the organization from SnelStart
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        disconnectSnelStart: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('disconnectSnelStart', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Whether the practice is connected to SnelStart, by which route, which subscription key that route still lacks, and the grootboekrekening chosen for verzekeraarfacturen (#4725).  Neither the koppelingssleutel nor a subscription key is ever returned. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Get the organization\'s SnelStart connection
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSnelStartConnection: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getSnelStartConnection', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every zorgverzekeraar the practice may book a verzekeraarfactuur for, grouped under its concern, with the SnelStart relatie it is booked on (#4726). An insurer\'s own relatie wins over its concern\'s. Groups and insurers the practice has billed come first, and `unmappedBilledInsurerIds` names the billed insurers no relatie resolves for yet. Read from Scrivio only; SnelStart is not asked. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary List the SnelStart relatie each zorgverzekeraar is booked on
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSnelStartRelatieMappings: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('getSnelStartRelatieMappings', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart/relatie-mappings`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The administratie\'s active grootboekrekeningen, read from SnelStart: revenue accounts (every `Verkopen…` grootboekfunctie) first, the rest below, each group by nummer. 8005 is suggested when the administratie has it; the practice still confirms the choice.  Also reports what became of the stored choice — a `NONACTIEF` or `VERDWENEN` account refuses booking until a new one is chosen. Without a working connection the list is empty and `check` says why. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary List the grootboekrekeningen a verzekeraarfactuur can be booked on
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSnelStartGrootboeken: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('listSnelStartGrootboeken', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart/grootboeken`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sets the relatie for every label of the concern that has none of its own. A relatiecode Scrivio already holds for a different SnelStart relatie is a `409` naming it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Book a zorgverzekeraar concern on a SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} concernId The zorgverzekeraar concern (&#x60;insurer_concerns.id&#x60;)
         * @param {SnelStartRelatieRef} snelStartRelatieRef 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        mapSnelStartRelatieToConcern: async (organizationId: string, concernId: string, snelStartRelatieRef: SnelStartRelatieRef, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('mapSnelStartRelatieToConcern', 'organizationId', organizationId)
            // verify required parameter 'concernId' is not null or undefined
            assertParamExists('mapSnelStartRelatieToConcern', 'concernId', concernId)
            // verify required parameter 'snelStartRelatieRef' is not null or undefined
            assertParamExists('mapSnelStartRelatieToConcern', 'snelStartRelatieRef', snelStartRelatieRef)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart/relatie-mappings/concerns/{concernId}`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"concernId"}}`, encodeURIComponent(String(concernId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(snelStartRelatieRef, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Sets the insurer\'s own relatie, which overrides its concern\'s. An insurer is booked on at most one relatie, so this moves it from any relatie it was on. A relatiecode Scrivio already holds for a different SnelStart relatie is a `409` naming it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Book a zorgverzekeraar on a SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} insurerId The zorgverzekeraar (&#x60;insurers.id&#x60;)
         * @param {SnelStartRelatieRef} snelStartRelatieRef 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        mapSnelStartRelatieToInsurer: async (organizationId: string, insurerId: string, snelStartRelatieRef: SnelStartRelatieRef, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('mapSnelStartRelatieToInsurer', 'organizationId', organizationId)
            // verify required parameter 'insurerId' is not null or undefined
            assertParamExists('mapSnelStartRelatieToInsurer', 'insurerId', insurerId)
            // verify required parameter 'snelStartRelatieRef' is not null or undefined
            assertParamExists('mapSnelStartRelatieToInsurer', 'snelStartRelatieRef', snelStartRelatieRef)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart/relatie-mappings/insurers/{insurerId}`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"insurerId"}}`, encodeURIComponent(String(insurerId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(snelStartRelatieRef, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Searches the administratie for active klanten whose naam contains `zoekterm`, or whose relatiecode it is — the relatie picker on the Integraties tab (#4726). A verkoopboeking can only be booked on a klant, so nothing else is returned. Without a working connection the list is empty and `check` says why; the picker then offers a relatiecode typed by hand. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Search the practice\'s SnelStart relaties
         * @param {string} organizationId Organization ID
         * @param {string} zoekterm Part of the relatie\&#39;s naam, or its relatiecode.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchSnelStartRelaties: async (organizationId: string, zoekterm: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('searchSnelStartRelaties', 'organizationId', organizationId)
            // verify required parameter 'zoekterm' is not null or undefined
            assertParamExists('searchSnelStartRelaties', 'zoekterm', zoekterm)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart/relaties`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (zoekterm !== undefined) {
                localVarQueryParameter['zoekterm'] = zoekterm;
            }

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Issues a single-use `referenceKey`, bound to this organisation for 30 minutes, and answers the SnelStart page to send the browser to (#4758). There the practice logs in, picks one administratie and agrees; SnelStart then POSTs the koppelingssleutel server-side to `/webhooks/snelstart` (`openapi-internal.yaml`) and returns the browser to the SnelStart tab. The return URL never carries a key.  A key delivered this way books nothing until an admin confirms its administratie (`/confirm`). A `409` where this deployment offers no productiekoppeling — see `activationAvailable`. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Start the SnelStart productiekoppeling (\"Koppelen met SnelStart\")
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        startSnelStartActivation: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('startSnelStartActivation', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart/activation`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Exchanges the stored koppelingssleutel for a token and reads one relatie, sending the subscription key of the connection\'s route — so a success proves the pair of keys works. A failure is a structured outcome, not an error. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Test the organization\'s SnelStart connection
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testSnelStartConnection: async (organizationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('testSnelStartConnection', 'organizationId', organizationId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart/test`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Labels with a relatie of their own keep it. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Remove a zorgverzekeraar concern\'s SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} concernId The zorgverzekeraar concern (&#x60;insurer_concerns.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unmapSnelStartRelatieFromConcern: async (organizationId: string, concernId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('unmapSnelStartRelatieFromConcern', 'organizationId', organizationId)
            // verify required parameter 'concernId' is not null or undefined
            assertParamExists('unmapSnelStartRelatieFromConcern', 'concernId', concernId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart/relatie-mappings/concerns/{concernId}`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"concernId"}}`, encodeURIComponent(String(concernId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The insurer falls back to its concern\'s relatie, if any. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Remove a zorgverzekeraar\'s own SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} insurerId The zorgverzekeraar (&#x60;insurers.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unmapSnelStartRelatieFromInsurer: async (organizationId: string, insurerId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationId' is not null or undefined
            assertParamExists('unmapSnelStartRelatieFromInsurer', 'organizationId', organizationId)
            // verify required parameter 'insurerId' is not null or undefined
            assertParamExists('unmapSnelStartRelatieFromInsurer', 'insurerId', insurerId)
            const localVarPath = `/api/v1/organizations/{organizationId}/integrations/snelstart/relatie-mappings/insurers/{insurerId}`
                .replace(`{${"organizationId"}}`, encodeURIComponent(String(organizationId)))
                .replace(`{${"insurerId"}}`, encodeURIComponent(String(insurerId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SnelstartIntegrationApi - functional programming interface
 */
export const SnelstartIntegrationApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SnelstartIntegrationApiAxiosParamCreator(configuration)
    return {
        /**
         * Records the grootboekrekening with SnelStart\'s `id`, which is what a verkoopboeking references. The nummer and omschrijving stored beside it are read back from SnelStart, for display only. An id that is not an active account in the administratie is a `400`; a connection that cannot be read is a `409`. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Choose the grootboekrekening for verzekeraarfacturen
         * @param {string} organizationId Organization ID
         * @param {SnelStartGrootboekChoiceRequest} snelStartGrootboekChoiceRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async chooseSnelStartGrootboek(organizationId: string, snelStartGrootboekChoiceRequest: SnelStartGrootboekChoiceRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartConnectionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.chooseSnelStartGrootboek(organizationId, snelStartGrootboekChoiceRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.chooseSnelStartGrootboek']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * SnelStart\'s webhook carries no signature, so a key it delivered waits for a person to recognise the administratie it belongs to (`administratieNaam`) before anything is booked with it (#4758). A `409` when nothing awaits confirmation. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Confirm the administratie a productiekoppeling connected to
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async confirmSnelStartAdministratie(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartConnectionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.confirmSnelStartAdministratie(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.confirmSnelStartAdministratie']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Stores a koppelingssleutel (maatwerksleutel) the practice generated in SnelStart under Koppelingen → Maatwerk, replacing any stored one. The key is written to Secret Manager and never persisted in the database or returned.  A connection made this way is a `MAATWERK` connection, which sends the subscription key a Scrivio platform admin stored for this organisation. The productiekoppeling (#4758) stores its key through the same path with route `PRODUCTIE`.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Connect the organization to SnelStart with a koppelingssleutel
         * @param {string} organizationId Organization ID
         * @param {SnelStartConnectRequest} snelStartConnectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async connectSnelStart(organizationId: string, snelStartConnectRequest: SnelStartConnectRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartConnectionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.connectSnelStart(organizationId, snelStartConnectRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.connectSnelStart']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deletes the koppelingssleutel from Secret Manager and forgets the grootboekrekening chosen in that administratie. Idempotent. The maatwerk subscription key is Scrivio\'s and stays; a platform admin removes it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Disconnect the organization from SnelStart
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async disconnectSnelStart(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.disconnectSnelStart(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.disconnectSnelStart']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Whether the practice is connected to SnelStart, by which route, which subscription key that route still lacks, and the grootboekrekening chosen for verzekeraarfacturen (#4725).  Neither the koppelingssleutel nor a subscription key is ever returned. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Get the organization\'s SnelStart connection
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getSnelStartConnection(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartConnectionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getSnelStartConnection(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.getSnelStartConnection']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every zorgverzekeraar the practice may book a verzekeraarfactuur for, grouped under its concern, with the SnelStart relatie it is booked on (#4726). An insurer\'s own relatie wins over its concern\'s. Groups and insurers the practice has billed come first, and `unmappedBilledInsurerIds` names the billed insurers no relatie resolves for yet. Read from Scrivio only; SnelStart is not asked. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary List the SnelStart relatie each zorgverzekeraar is booked on
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getSnelStartRelatieMappings(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartRelatieMappingsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getSnelStartRelatieMappings(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.getSnelStartRelatieMappings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The administratie\'s active grootboekrekeningen, read from SnelStart: revenue accounts (every `Verkopen…` grootboekfunctie) first, the rest below, each group by nummer. 8005 is suggested when the administratie has it; the practice still confirms the choice.  Also reports what became of the stored choice — a `NONACTIEF` or `VERDWENEN` account refuses booking until a new one is chosen. Without a working connection the list is empty and `check` says why. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary List the grootboekrekeningen a verzekeraarfactuur can be booked on
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listSnelStartGrootboeken(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartGrootboekenResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listSnelStartGrootboeken(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.listSnelStartGrootboeken']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sets the relatie for every label of the concern that has none of its own. A relatiecode Scrivio already holds for a different SnelStart relatie is a `409` naming it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Book a zorgverzekeraar concern on a SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} concernId The zorgverzekeraar concern (&#x60;insurer_concerns.id&#x60;)
         * @param {SnelStartRelatieRef} snelStartRelatieRef 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async mapSnelStartRelatieToConcern(organizationId: string, concernId: string, snelStartRelatieRef: SnelStartRelatieRef, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartRelatieMappingsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.mapSnelStartRelatieToConcern(organizationId, concernId, snelStartRelatieRef, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.mapSnelStartRelatieToConcern']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Sets the insurer\'s own relatie, which overrides its concern\'s. An insurer is booked on at most one relatie, so this moves it from any relatie it was on. A relatiecode Scrivio already holds for a different SnelStart relatie is a `409` naming it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Book a zorgverzekeraar on a SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} insurerId The zorgverzekeraar (&#x60;insurers.id&#x60;)
         * @param {SnelStartRelatieRef} snelStartRelatieRef 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async mapSnelStartRelatieToInsurer(organizationId: string, insurerId: string, snelStartRelatieRef: SnelStartRelatieRef, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartRelatieMappingsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.mapSnelStartRelatieToInsurer(organizationId, insurerId, snelStartRelatieRef, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.mapSnelStartRelatieToInsurer']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Searches the administratie for active klanten whose naam contains `zoekterm`, or whose relatiecode it is — the relatie picker on the Integraties tab (#4726). A verkoopboeking can only be booked on a klant, so nothing else is returned. Without a working connection the list is empty and `check` says why; the picker then offers a relatiecode typed by hand. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Search the practice\'s SnelStart relaties
         * @param {string} organizationId Organization ID
         * @param {string} zoekterm Part of the relatie\&#39;s naam, or its relatiecode.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async searchSnelStartRelaties(organizationId: string, zoekterm: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartRelatieSearchResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.searchSnelStartRelaties(organizationId, zoekterm, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.searchSnelStartRelaties']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Issues a single-use `referenceKey`, bound to this organisation for 30 minutes, and answers the SnelStart page to send the browser to (#4758). There the practice logs in, picks one administratie and agrees; SnelStart then POSTs the koppelingssleutel server-side to `/webhooks/snelstart` (`openapi-internal.yaml`) and returns the browser to the SnelStart tab. The return URL never carries a key.  A key delivered this way books nothing until an admin confirms its administratie (`/confirm`). A `409` where this deployment offers no productiekoppeling — see `activationAvailable`. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Start the SnelStart productiekoppeling (\"Koppelen met SnelStart\")
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async startSnelStartActivation(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartActivationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.startSnelStartActivation(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.startSnelStartActivation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Exchanges the stored koppelingssleutel for a token and reads one relatie, sending the subscription key of the connection\'s route — so a success proves the pair of keys works. A failure is a structured outcome, not an error. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Test the organization\'s SnelStart connection
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async testSnelStartConnection(organizationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartCheckResult>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.testSnelStartConnection(organizationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.testSnelStartConnection']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Labels with a relatie of their own keep it. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Remove a zorgverzekeraar concern\'s SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} concernId The zorgverzekeraar concern (&#x60;insurer_concerns.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unmapSnelStartRelatieFromConcern(organizationId: string, concernId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartRelatieMappingsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unmapSnelStartRelatieFromConcern(organizationId, concernId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.unmapSnelStartRelatieFromConcern']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The insurer falls back to its concern\'s relatie, if any. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Remove a zorgverzekeraar\'s own SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} insurerId The zorgverzekeraar (&#x60;insurers.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async unmapSnelStartRelatieFromInsurer(organizationId: string, insurerId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<SnelStartRelatieMappingsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.unmapSnelStartRelatieFromInsurer(organizationId, insurerId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SnelstartIntegrationApi.unmapSnelStartRelatieFromInsurer']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SnelstartIntegrationApi - factory interface
 */
export const SnelstartIntegrationApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SnelstartIntegrationApiFp(configuration)
    return {
        /**
         * Records the grootboekrekening with SnelStart\'s `id`, which is what a verkoopboeking references. The nummer and omschrijving stored beside it are read back from SnelStart, for display only. An id that is not an active account in the administratie is a `400`; a connection that cannot be read is a `409`. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Choose the grootboekrekening for verzekeraarfacturen
         * @param {string} organizationId Organization ID
         * @param {SnelStartGrootboekChoiceRequest} snelStartGrootboekChoiceRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        chooseSnelStartGrootboek(organizationId: string, snelStartGrootboekChoiceRequest: SnelStartGrootboekChoiceRequest, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartConnectionResponse> {
            return localVarFp.chooseSnelStartGrootboek(organizationId, snelStartGrootboekChoiceRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * SnelStart\'s webhook carries no signature, so a key it delivered waits for a person to recognise the administratie it belongs to (`administratieNaam`) before anything is booked with it (#4758). A `409` when nothing awaits confirmation. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Confirm the administratie a productiekoppeling connected to
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        confirmSnelStartAdministratie(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartConnectionResponse> {
            return localVarFp.confirmSnelStartAdministratie(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Stores a koppelingssleutel (maatwerksleutel) the practice generated in SnelStart under Koppelingen → Maatwerk, replacing any stored one. The key is written to Secret Manager and never persisted in the database or returned.  A connection made this way is a `MAATWERK` connection, which sends the subscription key a Scrivio platform admin stored for this organisation. The productiekoppeling (#4758) stores its key through the same path with route `PRODUCTIE`.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Connect the organization to SnelStart with a koppelingssleutel
         * @param {string} organizationId Organization ID
         * @param {SnelStartConnectRequest} snelStartConnectRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        connectSnelStart(organizationId: string, snelStartConnectRequest: SnelStartConnectRequest, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartConnectionResponse> {
            return localVarFp.connectSnelStart(organizationId, snelStartConnectRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Deletes the koppelingssleutel from Secret Manager and forgets the grootboekrekening chosen in that administratie. Idempotent. The maatwerk subscription key is Scrivio\'s and stays; a platform admin removes it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Disconnect the organization from SnelStart
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        disconnectSnelStart(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.disconnectSnelStart(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Whether the practice is connected to SnelStart, by which route, which subscription key that route still lacks, and the grootboekrekening chosen for verzekeraarfacturen (#4725).  Neither the koppelingssleutel nor a subscription key is ever returned. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Get the organization\'s SnelStart connection
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSnelStartConnection(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartConnectionResponse> {
            return localVarFp.getSnelStartConnection(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Every zorgverzekeraar the practice may book a verzekeraarfactuur for, grouped under its concern, with the SnelStart relatie it is booked on (#4726). An insurer\'s own relatie wins over its concern\'s. Groups and insurers the practice has billed come first, and `unmappedBilledInsurerIds` names the billed insurers no relatie resolves for yet. Read from Scrivio only; SnelStart is not asked. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary List the SnelStart relatie each zorgverzekeraar is booked on
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getSnelStartRelatieMappings(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartRelatieMappingsResponse> {
            return localVarFp.getSnelStartRelatieMappings(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * The administratie\'s active grootboekrekeningen, read from SnelStart: revenue accounts (every `Verkopen…` grootboekfunctie) first, the rest below, each group by nummer. 8005 is suggested when the administratie has it; the practice still confirms the choice.  Also reports what became of the stored choice — a `NONACTIEF` or `VERDWENEN` account refuses booking until a new one is chosen. Without a working connection the list is empty and `check` says why. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary List the grootboekrekeningen a verzekeraarfactuur can be booked on
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listSnelStartGrootboeken(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartGrootboekenResponse> {
            return localVarFp.listSnelStartGrootboeken(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Sets the relatie for every label of the concern that has none of its own. A relatiecode Scrivio already holds for a different SnelStart relatie is a `409` naming it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Book a zorgverzekeraar concern on a SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} concernId The zorgverzekeraar concern (&#x60;insurer_concerns.id&#x60;)
         * @param {SnelStartRelatieRef} snelStartRelatieRef 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        mapSnelStartRelatieToConcern(organizationId: string, concernId: string, snelStartRelatieRef: SnelStartRelatieRef, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartRelatieMappingsResponse> {
            return localVarFp.mapSnelStartRelatieToConcern(organizationId, concernId, snelStartRelatieRef, options).then((request) => request(axios, basePath));
        },
        /**
         * Sets the insurer\'s own relatie, which overrides its concern\'s. An insurer is booked on at most one relatie, so this moves it from any relatie it was on. A relatiecode Scrivio already holds for a different SnelStart relatie is a `409` naming it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Book a zorgverzekeraar on a SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} insurerId The zorgverzekeraar (&#x60;insurers.id&#x60;)
         * @param {SnelStartRelatieRef} snelStartRelatieRef 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        mapSnelStartRelatieToInsurer(organizationId: string, insurerId: string, snelStartRelatieRef: SnelStartRelatieRef, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartRelatieMappingsResponse> {
            return localVarFp.mapSnelStartRelatieToInsurer(organizationId, insurerId, snelStartRelatieRef, options).then((request) => request(axios, basePath));
        },
        /**
         * Searches the administratie for active klanten whose naam contains `zoekterm`, or whose relatiecode it is — the relatie picker on the Integraties tab (#4726). A verkoopboeking can only be booked on a klant, so nothing else is returned. Without a working connection the list is empty and `check` says why; the picker then offers a relatiecode typed by hand. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Search the practice\'s SnelStart relaties
         * @param {string} organizationId Organization ID
         * @param {string} zoekterm Part of the relatie\&#39;s naam, or its relatiecode.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        searchSnelStartRelaties(organizationId: string, zoekterm: string, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartRelatieSearchResponse> {
            return localVarFp.searchSnelStartRelaties(organizationId, zoekterm, options).then((request) => request(axios, basePath));
        },
        /**
         * Issues a single-use `referenceKey`, bound to this organisation for 30 minutes, and answers the SnelStart page to send the browser to (#4758). There the practice logs in, picks one administratie and agrees; SnelStart then POSTs the koppelingssleutel server-side to `/webhooks/snelstart` (`openapi-internal.yaml`) and returns the browser to the SnelStart tab. The return URL never carries a key.  A key delivered this way books nothing until an admin confirms its administratie (`/confirm`). A `409` where this deployment offers no productiekoppeling — see `activationAvailable`. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Start the SnelStart productiekoppeling (\"Koppelen met SnelStart\")
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        startSnelStartActivation(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartActivationResponse> {
            return localVarFp.startSnelStartActivation(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Exchanges the stored koppelingssleutel for a token and reads one relatie, sending the subscription key of the connection\'s route — so a success proves the pair of keys works. A failure is a structured outcome, not an error. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Test the organization\'s SnelStart connection
         * @param {string} organizationId Organization ID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        testSnelStartConnection(organizationId: string, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartCheckResult> {
            return localVarFp.testSnelStartConnection(organizationId, options).then((request) => request(axios, basePath));
        },
        /**
         * Labels with a relatie of their own keep it. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Remove a zorgverzekeraar concern\'s SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} concernId The zorgverzekeraar concern (&#x60;insurer_concerns.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unmapSnelStartRelatieFromConcern(organizationId: string, concernId: string, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartRelatieMappingsResponse> {
            return localVarFp.unmapSnelStartRelatieFromConcern(organizationId, concernId, options).then((request) => request(axios, basePath));
        },
        /**
         * The insurer falls back to its concern\'s relatie, if any. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
         * @summary Remove a zorgverzekeraar\'s own SnelStart relatie
         * @param {string} organizationId Organization ID
         * @param {string} insurerId The zorgverzekeraar (&#x60;insurers.id&#x60;)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        unmapSnelStartRelatieFromInsurer(organizationId: string, insurerId: string, options?: RawAxiosRequestConfig): AxiosPromise<SnelStartRelatieMappingsResponse> {
            return localVarFp.unmapSnelStartRelatieFromInsurer(organizationId, insurerId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SnelstartIntegrationApi - object-oriented interface
 */
export class SnelstartIntegrationApi extends BaseAPI {
    /**
     * Records the grootboekrekening with SnelStart\'s `id`, which is what a verkoopboeking references. The nummer and omschrijving stored beside it are read back from SnelStart, for display only. An id that is not an active account in the administratie is a `400`; a connection that cannot be read is a `409`. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Choose the grootboekrekening for verzekeraarfacturen
     * @param {string} organizationId Organization ID
     * @param {SnelStartGrootboekChoiceRequest} snelStartGrootboekChoiceRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public chooseSnelStartGrootboek(organizationId: string, snelStartGrootboekChoiceRequest: SnelStartGrootboekChoiceRequest, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).chooseSnelStartGrootboek(organizationId, snelStartGrootboekChoiceRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * SnelStart\'s webhook carries no signature, so a key it delivered waits for a person to recognise the administratie it belongs to (`administratieNaam`) before anything is booked with it (#4758). A `409` when nothing awaits confirmation. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Confirm the administratie a productiekoppeling connected to
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public confirmSnelStartAdministratie(organizationId: string, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).confirmSnelStartAdministratie(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Stores a koppelingssleutel (maatwerksleutel) the practice generated in SnelStart under Koppelingen → Maatwerk, replacing any stored one. The key is written to Secret Manager and never persisted in the database or returned.  A connection made this way is a `MAATWERK` connection, which sends the subscription key a Scrivio platform admin stored for this organisation. The productiekoppeling (#4758) stores its key through the same path with route `PRODUCTIE`.  Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Connect the organization to SnelStart with a koppelingssleutel
     * @param {string} organizationId Organization ID
     * @param {SnelStartConnectRequest} snelStartConnectRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public connectSnelStart(organizationId: string, snelStartConnectRequest: SnelStartConnectRequest, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).connectSnelStart(organizationId, snelStartConnectRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deletes the koppelingssleutel from Secret Manager and forgets the grootboekrekening chosen in that administratie. Idempotent. The maatwerk subscription key is Scrivio\'s and stays; a platform admin removes it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Disconnect the organization from SnelStart
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public disconnectSnelStart(organizationId: string, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).disconnectSnelStart(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Whether the practice is connected to SnelStart, by which route, which subscription key that route still lacks, and the grootboekrekening chosen for verzekeraarfacturen (#4725).  Neither the koppelingssleutel nor a subscription key is ever returned. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Get the organization\'s SnelStart connection
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getSnelStartConnection(organizationId: string, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).getSnelStartConnection(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every zorgverzekeraar the practice may book a verzekeraarfactuur for, grouped under its concern, with the SnelStart relatie it is booked on (#4726). An insurer\'s own relatie wins over its concern\'s. Groups and insurers the practice has billed come first, and `unmappedBilledInsurerIds` names the billed insurers no relatie resolves for yet. Read from Scrivio only; SnelStart is not asked. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary List the SnelStart relatie each zorgverzekeraar is booked on
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getSnelStartRelatieMappings(organizationId: string, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).getSnelStartRelatieMappings(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The administratie\'s active grootboekrekeningen, read from SnelStart: revenue accounts (every `Verkopen…` grootboekfunctie) first, the rest below, each group by nummer. 8005 is suggested when the administratie has it; the practice still confirms the choice.  Also reports what became of the stored choice — a `NONACTIEF` or `VERDWENEN` account refuses booking until a new one is chosen. Without a working connection the list is empty and `check` says why. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary List the grootboekrekeningen a verzekeraarfactuur can be booked on
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listSnelStartGrootboeken(organizationId: string, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).listSnelStartGrootboeken(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sets the relatie for every label of the concern that has none of its own. A relatiecode Scrivio already holds for a different SnelStart relatie is a `409` naming it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Book a zorgverzekeraar concern on a SnelStart relatie
     * @param {string} organizationId Organization ID
     * @param {string} concernId The zorgverzekeraar concern (&#x60;insurer_concerns.id&#x60;)
     * @param {SnelStartRelatieRef} snelStartRelatieRef 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public mapSnelStartRelatieToConcern(organizationId: string, concernId: string, snelStartRelatieRef: SnelStartRelatieRef, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).mapSnelStartRelatieToConcern(organizationId, concernId, snelStartRelatieRef, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Sets the insurer\'s own relatie, which overrides its concern\'s. An insurer is booked on at most one relatie, so this moves it from any relatie it was on. A relatiecode Scrivio already holds for a different SnelStart relatie is a `409` naming it. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Book a zorgverzekeraar on a SnelStart relatie
     * @param {string} organizationId Organization ID
     * @param {string} insurerId The zorgverzekeraar (&#x60;insurers.id&#x60;)
     * @param {SnelStartRelatieRef} snelStartRelatieRef 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public mapSnelStartRelatieToInsurer(organizationId: string, insurerId: string, snelStartRelatieRef: SnelStartRelatieRef, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).mapSnelStartRelatieToInsurer(organizationId, insurerId, snelStartRelatieRef, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Searches the administratie for active klanten whose naam contains `zoekterm`, or whose relatiecode it is — the relatie picker on the Integraties tab (#4726). A verkoopboeking can only be booked on a klant, so nothing else is returned. Without a working connection the list is empty and `check` says why; the picker then offers a relatiecode typed by hand. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Search the practice\'s SnelStart relaties
     * @param {string} organizationId Organization ID
     * @param {string} zoekterm Part of the relatie\&#39;s naam, or its relatiecode.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public searchSnelStartRelaties(organizationId: string, zoekterm: string, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).searchSnelStartRelaties(organizationId, zoekterm, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Issues a single-use `referenceKey`, bound to this organisation for 30 minutes, and answers the SnelStart page to send the browser to (#4758). There the practice logs in, picks one administratie and agrees; SnelStart then POSTs the koppelingssleutel server-side to `/webhooks/snelstart` (`openapi-internal.yaml`) and returns the browser to the SnelStart tab. The return URL never carries a key.  A key delivered this way books nothing until an admin confirms its administratie (`/confirm`). A `409` where this deployment offers no productiekoppeling — see `activationAvailable`. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Start the SnelStart productiekoppeling (\"Koppelen met SnelStart\")
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public startSnelStartActivation(organizationId: string, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).startSnelStartActivation(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Exchanges the stored koppelingssleutel for a token and reads one relatie, sending the subscription key of the connection\'s route — so a success proves the pair of keys works. A failure is a structured outcome, not an error. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Test the organization\'s SnelStart connection
     * @param {string} organizationId Organization ID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public testSnelStartConnection(organizationId: string, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).testSnelStartConnection(organizationId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Labels with a relatie of their own keep it. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Remove a zorgverzekeraar concern\'s SnelStart relatie
     * @param {string} organizationId Organization ID
     * @param {string} concernId The zorgverzekeraar concern (&#x60;insurer_concerns.id&#x60;)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unmapSnelStartRelatieFromConcern(organizationId: string, concernId: string, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).unmapSnelStartRelatieFromConcern(organizationId, concernId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The insurer falls back to its concern\'s relatie, if any. Idempotent. Requires the `integrations.manage` permission and a FULL license (ADR-023). 
     * @summary Remove a zorgverzekeraar\'s own SnelStart relatie
     * @param {string} organizationId Organization ID
     * @param {string} insurerId The zorgverzekeraar (&#x60;insurers.id&#x60;)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public unmapSnelStartRelatieFromInsurer(organizationId: string, insurerId: string, options?: RawAxiosRequestConfig) {
        return SnelstartIntegrationApiFp(this.configuration).unmapSnelStartRelatieFromInsurer(organizationId, insurerId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * UserIntegrationsApi - axios parameter creator
 */
export const UserIntegrationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns whether the authenticated user has opted in to sending via the organization\'s Zivver tenant, plus the outcome of the most recent address validation. Requires a FULL license and an organization that has Zivver configured (ADR-023, ADR-028); otherwise 403. 
         * @summary Get the current user\'s Zivver enablement status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyZivverStatus: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users/me/integrations/zivver`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Answers one question for the authenticated user: *may I offer the \"stuur naar huisarts\" action here?* (#4121, ADR-041).  It is a **capability read, not a configuration read**, and that distinction is the reason it exists beside `getZorgMailConfig`. The configuration endpoint requires `integrations.manage`, which only an Administrator holds — so a behandelaar, the one person this feature is for, cannot read it and would never be offered the action. This is the `me`-scoped sibling of `getMyZivverStatus`: any authenticated user may ask it about themselves.  There is deliberately **no per-user enablement** to report, unlike Zivver\'s: a ZorgMail afzender is a name in the message payload with nothing to verify against, so every member of a configured practice can send (ADR-041 §3). `available` therefore says only that the practice has a FULL license and a recorded ZorgMail Account-ID.  A practice that has neither answers `false` rather than 403: \"can I?\" has an answer, and it is *no*. Every gate is re-enforced when the letter is actually sent. 
         * @summary Whether the caller can send a letter over ZorgMail, and whether the inbox is offered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyZorgMailAvailability: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users/me/integrations/zorgmail`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Opt the authenticated user in or out of sending via the organization\'s Zivver tenant. On enable, the backend validates that the caller\'s email exists as an active user in the organization\'s Zivver tenant (SCIM v2): a match stores the validated sender address; no match returns 422 (`zivver-account-not-found`); if validation cannot be performed (SCIM credentials unavailable) the user is still enabled but left unvalidated. Requires a FULL license and an organization that has Zivver configured (ADR-023, ADR-028). 
         * @summary Enable or disable Zivver sending for the current user
         * @param {UserZivverUpdateRequest} userZivverUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMyZivverStatus: async (userZivverUpdateRequest: UserZivverUpdateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'userZivverUpdateRequest' is not null or undefined
            assertParamExists('updateMyZivverStatus', 'userZivverUpdateRequest', userZivverUpdateRequest)
            const localVarPath = `/api/v1/users/me/integrations/zivver`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(userZivverUpdateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * UserIntegrationsApi - functional programming interface
 */
export const UserIntegrationsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = UserIntegrationsApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns whether the authenticated user has opted in to sending via the organization\'s Zivver tenant, plus the outcome of the most recent address validation. Requires a FULL license and an organization that has Zivver configured (ADR-023, ADR-028); otherwise 403. 
         * @summary Get the current user\'s Zivver enablement status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyZivverStatus(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserZivverStatusResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyZivverStatus(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserIntegrationsApi.getMyZivverStatus']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Answers one question for the authenticated user: *may I offer the \"stuur naar huisarts\" action here?* (#4121, ADR-041).  It is a **capability read, not a configuration read**, and that distinction is the reason it exists beside `getZorgMailConfig`. The configuration endpoint requires `integrations.manage`, which only an Administrator holds — so a behandelaar, the one person this feature is for, cannot read it and would never be offered the action. This is the `me`-scoped sibling of `getMyZivverStatus`: any authenticated user may ask it about themselves.  There is deliberately **no per-user enablement** to report, unlike Zivver\'s: a ZorgMail afzender is a name in the message payload with nothing to verify against, so every member of a configured practice can send (ADR-041 §3). `available` therefore says only that the practice has a FULL license and a recorded ZorgMail Account-ID.  A practice that has neither answers `false` rather than 403: \"can I?\" has an answer, and it is *no*. Every gate is re-enforced when the letter is actually sent. 
         * @summary Whether the caller can send a letter over ZorgMail, and whether the inbox is offered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMyZorgMailAvailability(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserZorgMailAvailabilityResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMyZorgMailAvailability(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserIntegrationsApi.getMyZorgMailAvailability']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Opt the authenticated user in or out of sending via the organization\'s Zivver tenant. On enable, the backend validates that the caller\'s email exists as an active user in the organization\'s Zivver tenant (SCIM v2): a match stores the validated sender address; no match returns 422 (`zivver-account-not-found`); if validation cannot be performed (SCIM credentials unavailable) the user is still enabled but left unvalidated. Requires a FULL license and an organization that has Zivver configured (ADR-023, ADR-028). 
         * @summary Enable or disable Zivver sending for the current user
         * @param {UserZivverUpdateRequest} userZivverUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateMyZivverStatus(userZivverUpdateRequest: UserZivverUpdateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UserZivverStatusResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateMyZivverStatus(userZivverUpdateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['UserIntegrationsApi.updateMyZivverStatus']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * UserIntegrationsApi - factory interface
 */
export const UserIntegrationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = UserIntegrationsApiFp(configuration)
    return {
        /**
         * Returns whether the authenticated user has opted in to sending via the organization\'s Zivver tenant, plus the outcome of the most recent address validation. Requires a FULL license and an organization that has Zivver configured (ADR-023, ADR-028); otherwise 403. 
         * @summary Get the current user\'s Zivver enablement status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyZivverStatus(options?: RawAxiosRequestConfig): AxiosPromise<UserZivverStatusResponse> {
            return localVarFp.getMyZivverStatus(options).then((request) => request(axios, basePath));
        },
        /**
         * Answers one question for the authenticated user: *may I offer the \"stuur naar huisarts\" action here?* (#4121, ADR-041).  It is a **capability read, not a configuration read**, and that distinction is the reason it exists beside `getZorgMailConfig`. The configuration endpoint requires `integrations.manage`, which only an Administrator holds — so a behandelaar, the one person this feature is for, cannot read it and would never be offered the action. This is the `me`-scoped sibling of `getMyZivverStatus`: any authenticated user may ask it about themselves.  There is deliberately **no per-user enablement** to report, unlike Zivver\'s: a ZorgMail afzender is a name in the message payload with nothing to verify against, so every member of a configured practice can send (ADR-041 §3). `available` therefore says only that the practice has a FULL license and a recorded ZorgMail Account-ID.  A practice that has neither answers `false` rather than 403: \"can I?\" has an answer, and it is *no*. Every gate is re-enforced when the letter is actually sent. 
         * @summary Whether the caller can send a letter over ZorgMail, and whether the inbox is offered
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMyZorgMailAvailability(options?: RawAxiosRequestConfig): AxiosPromise<UserZorgMailAvailabilityResponse> {
            return localVarFp.getMyZorgMailAvailability(options).then((request) => request(axios, basePath));
        },
        /**
         * Opt the authenticated user in or out of sending via the organization\'s Zivver tenant. On enable, the backend validates that the caller\'s email exists as an active user in the organization\'s Zivver tenant (SCIM v2): a match stores the validated sender address; no match returns 422 (`zivver-account-not-found`); if validation cannot be performed (SCIM credentials unavailable) the user is still enabled but left unvalidated. Requires a FULL license and an organization that has Zivver configured (ADR-023, ADR-028). 
         * @summary Enable or disable Zivver sending for the current user
         * @param {UserZivverUpdateRequest} userZivverUpdateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateMyZivverStatus(userZivverUpdateRequest: UserZivverUpdateRequest, options?: RawAxiosRequestConfig): AxiosPromise<UserZivverStatusResponse> {
            return localVarFp.updateMyZivverStatus(userZivverUpdateRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * UserIntegrationsApi - object-oriented interface
 */
export class UserIntegrationsApi extends BaseAPI {
    /**
     * Returns whether the authenticated user has opted in to sending via the organization\'s Zivver tenant, plus the outcome of the most recent address validation. Requires a FULL license and an organization that has Zivver configured (ADR-023, ADR-028); otherwise 403. 
     * @summary Get the current user\'s Zivver enablement status
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyZivverStatus(options?: RawAxiosRequestConfig) {
        return UserIntegrationsApiFp(this.configuration).getMyZivverStatus(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Answers one question for the authenticated user: *may I offer the \"stuur naar huisarts\" action here?* (#4121, ADR-041).  It is a **capability read, not a configuration read**, and that distinction is the reason it exists beside `getZorgMailConfig`. The configuration endpoint requires `integrations.manage`, which only an Administrator holds — so a behandelaar, the one person this feature is for, cannot read it and would never be offered the action. This is the `me`-scoped sibling of `getMyZivverStatus`: any authenticated user may ask it about themselves.  There is deliberately **no per-user enablement** to report, unlike Zivver\'s: a ZorgMail afzender is a name in the message payload with nothing to verify against, so every member of a configured practice can send (ADR-041 §3). `available` therefore says only that the practice has a FULL license and a recorded ZorgMail Account-ID.  A practice that has neither answers `false` rather than 403: \"can I?\" has an answer, and it is *no*. Every gate is re-enforced when the letter is actually sent. 
     * @summary Whether the caller can send a letter over ZorgMail, and whether the inbox is offered
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMyZorgMailAvailability(options?: RawAxiosRequestConfig) {
        return UserIntegrationsApiFp(this.configuration).getMyZorgMailAvailability(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Opt the authenticated user in or out of sending via the organization\'s Zivver tenant. On enable, the backend validates that the caller\'s email exists as an active user in the organization\'s Zivver tenant (SCIM v2): a match stores the validated sender address; no match returns 422 (`zivver-account-not-found`); if validation cannot be performed (SCIM credentials unavailable) the user is still enabled but left unvalidated. Requires a FULL license and an organization that has Zivver configured (ADR-023, ADR-028). 
     * @summary Enable or disable Zivver sending for the current user
     * @param {UserZivverUpdateRequest} userZivverUpdateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateMyZivverStatus(userZivverUpdateRequest: UserZivverUpdateRequest, options?: RawAxiosRequestConfig) {
        return UserIntegrationsApiFp(this.configuration).updateMyZivverStatus(userZivverUpdateRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



